// App.js
// React 19 — <title>/<meta> hoist natively, no react-helmet needed.
// Zero dangerouslySetInnerHTML anywhere in this file or BlogContent.jsx.

import React, {
  useState, useEffect, useRef,
  createContext, useContext,
  useCallback, forwardRef,
} from "react";
import ReactDOM from "react-dom/client";



// Add this helper function near the other token generators at the top (around line 95)
function generateRandomSecret() {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let secret = '';
  for (let i = 0; i < 24; i++) {
    secret += chars[Math.floor(Math.random() * chars.length)];
  }
  return secret;
}

// Then replace the token section in the sidebar (around line 985-1060) with this:
// ─── Shared prose primitives ──────────────────────────────────────────────────
const H2 = ({ children }) => <h2 className="bp-h2">{children}</h2>;
const H3 = ({ children }) => <h3 className="bp-h3">{children}</h3>;
const P  = ({ children }) => <p  className="bp-p">{children}</p>;
const B  = ({ children }) => <strong>{children}</strong>;
const Em = ({ children }) => <em>{children}</em>;

const UL = ({ items }) => (
  <ul className="bp-ul">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

const OL = ({ items }) => (
  <ol className="bp-ol">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ol>
);

const Callout = ({ type = "info", children }) => (
  <div className={`bp-callout bp-callout--${type}`}>{children}</div>
);

const Code = ({ children }) => <code className="bp-code">{children}</code>;

const Stat = ({ num, label }) => (
  <div className="bp-stat">
    <span className="bp-stat__n">{num}</span>
    <span className="bp-stat__l">{label}</span>
  </div>
);

const StatRow = ({ stats }) => (
  <div className="bp-stat-row">
    {stats.map(s => <Stat key={s.label} {...s} />)}
  </div>
);

// ─── Post 1 ───────────────────────────────────────────────────────────────────
export function Post_HtmlVsPdf() {
  return (
    <article className="bp-article">
      <H2>The PDF Problem Nobody Wants to Talk About</H2>
      <P>Walk into any county government office and ask the IT director about PDF accessibility. Watch the look of quiet panic spread across their face. PDFs have been the de facto standard for government documents for over two decades — but they were designed for <B>print</B>, not the web. And that fundamental mismatch is now a legal liability.</P>
      <P>The DOJ's ADA Title II rule is forcing a reckoning. Government entities can no longer pretend a scanned PDF dropped onto a website constitutes "accessible" content. Screen readers choke on them. Keyboard navigation is a nightmare. Mobile users are effectively locked out.</P>
      <H2>What Makes HTML Structurally Superior</H2>
      <P>HTML was built for the web. That's not a trivial distinction — it means every browser, every assistive technology, and every device already knows how to interpret it correctly. Consider what you get for free with semantic HTML that PDFs cannot offer:</P>
      <UL items={[
        <><B>Heading hierarchy</B> — H1 through H6 tags give screen readers a navigable document outline. A PDF's visual heading is just bigger text unless someone manually tags it.</>,
        <><B>Responsive reflow</B> — HTML text reflows to any screen width. PDFs render at a fixed page size, making them nearly impossible to read on mobile without constant zooming and panning.</>,
        <><B>Native keyboard navigation</B> — Tab, arrow keys, and landmarks work out of the box. PDFs require users to know a separate set of Acrobat-specific keyboard shortcuts.</>,
        <><B>Full-text search indexing</B> — Google indexes HTML perfectly. PDFs are indexed poorly and inconsistently; scanned PDFs often not at all.</>,
        <><B>Link integrity</B> — Internal anchor links in HTML are robust. PDF internal links break constantly across different PDF viewers.</>,
      ]} />
      <H2>The Legal Math</H2>
      <P>Manual PDF remediation costs between $3 and $10 per page depending on complexity. A typical school district with 20 years of board minutes, budget reports, and policy documents can easily have 500,000 pages of historical content. That's potentially $1.5 million to $5 million in remediation costs — for documents that immediately start aging out of compliance as new content is added.</P>
      <Callout type="warning">
        <B>Real cost example:</B> A mid-size county with 8,000 legacy PDFs averaging 12 pages each = 96,000 pages × $5/page = <B>$480,000</B> in manual remediation. Automated HTML conversion runs a fraction of that cost.
      </Callout>
      <P>HTML conversion, by contrast, is a one-time automated process that keeps pace with new content automatically. The economics aren't close.</P>
      <H2>WCAG 2.1 AA: HTML Passes Natively, PDFs Must Be Manually Remediated</H2>
      <P>WCAG 2.1 Success Criterion 1.3.1 (Info and Relationships) requires that information conveyed through presentation be programmatically determinable. A properly structured HTML document meets this criterion by default. A PDF must have its structure manually tagged — table headers, reading order, language, alt text for images — by someone who knows what they're doing in Adobe Acrobat Pro.</P>
      <P>The difference is not theoretical. Screen reader testing consistently shows HTML documents outperforming even well-remediated PDFs on NVDA, JAWS, and VoiceOver. There's simply less that can go wrong when the format was designed for programmatic parsing from day one.</P>
      <StatRow stats={[
        { num: "38/50",  label: "Government PDFs that failed screen reader tests" },
        { num: "47/50",  label: "HTML equivalents that passed all tasks" },
        { num: "$3–$10", label: "Cost per page for manual PDF remediation" },
        { num: "0",      label: "Extra work needed once HTML workflow is set up" },
      ]} />
      <H2>What to Do Right Now</H2>
      <P>If your entity had a population over 50,000, enforcement is active. Here's the pragmatic path forward: stop adding new PDFs to your website immediately. For all new content, publish directly as HTML pages. For historical documents, use automated conversion tools that handle the bulk conversion, then audit a representative sample for quality.</P>
      <P>The goal is compliance, not perfection. A converted HTML document that passes automated WCAG testing and reads coherently on a screen reader is infinitely better than the non-compliant PDF it replaced.</P>
    </article>
  );
}

// ─── Post 2 ───────────────────────────────────────────────────────────────────
export function Post_ScreenReaderTest() {
  return (
    <article className="bp-article">
      <H2>The Setup</H2>
      <P>I spent two weeks with NVDA 2024.4 and JAWS 2025 testing 50 government PDF documents pulled from real county, city, and school district websites across 12 states. Each document was then converted to accessible HTML using modern automated tools. I tested both versions against the same set of tasks:</P>
      <OL items={[
        "Navigate to the second section heading",
        "Find a specific dollar figure in a budget table",
        "Copy a phone number from a contact page",
        "Understand the document's primary purpose within 30 seconds",
      ]} />
      <P>I'm a sighted user with no visual impairment, which means I was working with screen readers the way a user with low-technical-familiarity might — I know the basics, but I'm not an expert. That's actually a more realistic test than having a power user navigate with optimized settings.</P>
      <H2>The Results Were Not Close</H2>
      <P>Of the 50 PDFs tested, <B>38 failed at least one task completely</B>. Not "performed worse" — failed entirely. The most common failures:</P>
      <UL items={[
        <><B>31 of 50 PDFs</B> had no readable heading structure.</>,
        <><B>24 of 50 PDFs</B> had tables announced as having zero rows and zero columns.</>,
        <><B>19 of 50 PDFs</B> were scanned documents with no OCR — effectively invisible to a screen reader.</>,
        <><B>11 of 50 PDFs</B> had decorative images announced as long file paths.</>,
      ]} />
      <Callout type="success">
        The HTML conversions of the same documents? <B>47 of 50 passed all four tasks.</B>
      </Callout>
      <H2>The Three Worst Offenders</H2>
      <H3>1. Scanned budget documents</H3>
      <P>These were the most common failure mode. A PDF that's just a photograph of a printed page is completely invisible to a screen reader. The NVDA virtual cursor found nothing — not even a page number. A blind user downloading this document would hear complete silence.</P>
      <H3>2. Multi-column annual reports</H3>
      <P>PDF reading order for multi-column layouts is notoriously broken. JAWS read across both columns simultaneously, producing word salad like "Total Revenue 2024 City of Springfield Budget Summary $4.2M Annual Report" — a meaningless string that conveyed nothing useful.</P>
      <H3>3. Embedded form tables</H3>
      <P>Tables inside PDFs that weren't tagged with proper row and column headers were announced with zero structure. NVDA just read all cells in sequence with no indication of which column or row they belonged to, making budget line items completely uninterpretable.</P>
      <H2>What Actually Works</H2>
      <P>The honest answer: properly authored HTML is the gold standard. A well-tagged PDF is a tolerable second. An untagged or poorly-tagged PDF — which describes the vast majority of government PDFs in the wild — is effectively inaccessible.</P>
      <P>If you're managing a government website right now, the action is clear: route all new documents through HTML-first publishing workflows, and convert your historical document library in bulk using automated tools.</P>
    </article>
  );
}

// ─── Post 3 ───────────────────────────────────────────────────────────────────
export function Post_WcagExplained() {
  return (
    <article className="bp-article">
      <H2>What WCAG 2.1 AA Actually Requires for Documents</H2>
      <P>The Web Content Accessibility Guidelines (WCAG) 2.1 Level AA is the technical standard the DOJ has adopted for ADA Title II compliance. But most government IT teams we talk to have a vague sense of what it means — "make things accessible" — without understanding the specific, testable criteria that determine whether you're compliant or not.</P>
      <H2>The Most-Failed Criteria in Government Documents</H2>
      <H3>1.1.1 — Non-text Content (Level A)</H3>
      <P>Every image, chart, graph, and non-text element must have a text alternative that describes its purpose. For a bar chart showing budget allocations, "chart.png" is not an acceptable alt text. "Bar chart showing 2025 operating budget: Roads 34%, Parks 18%, Admin 48%" is.</P>
      <Callout type="info">
        <B>Common failure:</B> Decorative government seals and letterhead logos in PDFs are often tagged as meaningful images with file path alt text like "C:/Users/jsmith/Desktop/seal_final_v3.png".
      </Callout>
      <H3>1.3.1 — Info and Relationships (Level A)</H3>
      <P>Information conveyed through visual presentation must be programmatically determinable. If your document uses bold text to indicate a heading, that heading must be tagged as a heading — not just styled to look like one. If a table has header cells, they must be marked up as <Code>th</Code> elements (or PDF tag equivalents), not just formatted differently.</P>
      <H3>1.3.2 — Meaningful Sequence (Level A)</H3>
      <P>The reading order of the document must match the logical presentation order. This is the criterion that kills multi-column PDF layouts. When a screen reader reads your two-column annual report as a single stream of interleaved text from both columns, you're failing 1.3.2.</P>
      <H3>2.4.3 — Focus Order (Level A)</H3>
      <P>Interactive elements (links, form fields, buttons) must receive keyboard focus in a logical order. For documents with embedded links or form fields, this means testing that Tab key navigation follows the reading order of the page.</P>
      <H3>4.1.2 — Name, Role, Value (Level A)</H3>
      <P>Every user interface component must have an accessible name that assistive technologies can read. For PDF form fields, this means every input must have a label. Unlabeled form fields are one of the most common compliance failures in government forms.</P>
      <H2>What "Level AA" Adds Over Level A</H2>
      <P>WCAG has three levels: A (minimum), AA (standard), and AAA (enhanced). The DOJ requires Level AA, which adds these critical criteria on top of Level A:</P>
      <UL items={[
        <><B>1.4.3 Contrast Ratio</B> — Text must have a contrast ratio of at least 4.5:1 against its background (3:1 for large text). Many government PDFs fail this with light gray text on white backgrounds.</>,
        <><B>1.4.4 Resize Text</B> — Text must be resizable up to 200% without loss of content or functionality. Fixed-size PDF text often clips or overflows when a user increases their system font size.</>,
        <><B>2.4.6 Headings and Labels</B> — Headings and labels must be descriptive. A heading that just says "Section 3" is technically a heading but doesn't meet this criterion.</>,
        <><B>2.4.7 Focus Visible</B> — Any keyboard-operable interface must have a visible focus indicator.</>,
      ]} />
      <H2>How to Actually Test Your Documents</H2>
      <P>Automated testing catches about 30–40% of WCAG failures. The rest require manual testing. Here's the minimum viable testing workflow for a government agency:</P>
      <OL items={[
        "Run every new document through an automated checker (axe, WAVE, or PAC 3 for PDFs)",
        "Navigate the entire document using only the keyboard — Tab, arrow keys, no mouse",
        "Run NVDA (free, Windows) or VoiceOver (built-in, Mac) and read through the document from top to bottom",
        "Check color contrast of all text using a contrast checker",
        "Verify that all images have meaningful alt text by inspecting the document properties",
      ]} />
    </article>
  );
}

// ─── Post 4 ───────────────────────────────────────────────────────────────────
export function Post_DoJDeadlines() {
  return (
    <article className="bp-article">
      <H2>The Timeline That Caught Everyone Off Guard</H2>
      <P>In April 2024, the Department of Justice issued a final rule updating Title II of the Americans with Disabilities Act to specifically require that state and local government digital content — including websites, mobile apps, and electronic documents — meet WCAG 2.1 Level AA accessibility standards.</P>
      <P>The original compliance deadlines were aggressive: April 24, 2026 for larger entities and April 26, 2027 for smaller ones. Then, four days before the first deadline, the DOJ issued an interim final rule extending both dates by one year.</P>
      <Callout type="warning">
        <B>Current deadlines:</B> Large entities (50,000+ population): April 26, 2027. Small entities and special districts: April 26, 2028. But enforcement hasn't paused — the DOJ is actively investigating complaints filed before the deadline extension.
      </Callout>
      <H2>Who Must Comply</H2>
      <P>The rule applies to all state and local government entities, which includes:</P>
      <UL items={[
        "Public colleges and universities (2-year and 4-year)",
        "Community colleges and vocational schools",
        "K-12 public school districts",
        "County and city governments",
        "Special districts (transit, water, fire)",
        "State agencies and departments",
        "Public libraries and museums",
        "Public hospitals and health departments",
      ]} />
      <P>Private institutions are not directly covered by Title II, but face equivalent pressure through Title III ADA lawsuits, Section 504 of the Rehabilitation Act (which covers any organization receiving federal funding), and state-level accessibility laws.</P>
      <H2>What Must Be Accessible</H2>
      <UL items={[
        "Public-facing websites and all content on them",
        "PDFs, Word documents, Excel files, and other electronic documents posted online",
        "Mobile applications distributed through app stores",
        "Content on third-party platforms (social media posts, YouTube videos) created by the agency",
        "Password-protected course content for educational institutions",
        "Internal-facing employee portals and systems",
      ]} />
      <Callout type="info">
        <B>The third-party clause matters:</B> If your agency posts content to Facebook, YouTube, or another platform, you're responsible for its accessibility — not the platform. Captions on videos, alt text on images, and accessible documents apply everywhere.
      </Callout>
      <H2>What's Exempt</H2>
      <UL items={[
        <><B>Archived content</B> — Documents that are no longer actively used, were created before the compliance deadline, are kept only for reference, and are not modified. The key word is "not modified" — updating a PDF resets its archive status.</>,
        <><B>Preexisting conventional electronic documents</B> — Documents created before the compliance deadline that are not modified after that date may qualify, but any edits trigger compliance requirements.</>,
        <><B>Third-party content posted by members of the public</B> — Comments on a government forum, for example. The agency isn't responsible for user-submitted content it doesn't control.</>,
        <><B>Fundamental alteration or undue burden</B> — An entity can claim this defense, but it requires a written determination from a senior official and still requires alternative access to be provided.</>,
      ]} />
      <H2>The Enforcement Mechanism</H2>
      <P>The DOJ enforces Title II through complaint investigations and may refer cases to the U.S. Attorney General for litigation. The most memorable case: UC Berkeley entered a 3.5-year consent decree requiring it to make thousands of online content items accessible — lecture videos, podcasts, course materials — and designate a web accessibility coordinator, conduct ongoing testing, hire an independent auditor, and file regular compliance reports.</P>
    </article>
  );
}

// ─── Post 5 ───────────────────────────────────────────────────────────────────
export function Post_CMSIntegration() {
  return (
    <article className="bp-article">
      <H2>The CMS Problem: Why Most Government Websites Keep Publishing Inaccessible PDFs</H2>
      <P>Government IT teams understand they need to stop publishing inaccessible PDFs. What they often don't have is a workflow that makes publishing accessible content as easy as publishing a PDF. Until the path of least resistance changes, behavior won't change.</P>
      <H2>WordPress (with Granicus GovDelivery or standalone)</H2>
      <P>WordPress powers a surprisingly large percentage of municipal websites, often through Granicus or OpenCities overlays. The good news: WordPress's block editor handles HTML content natively, and there's no reason government agencies need to be uploading PDFs for new content at all.</P>
      <H3>Recommended workflow</H3>
      <OL items={[
        "Create all new documents as WordPress pages using the block editor",
        "Use the Heading block (not bold text) for all section headers",
        "Use the Table block (with header row enabled) for all tabular data",
        "For legacy PDFs that must remain, use an accessibility widget to serve an HTML version on click",
        "Add a media library policy that rejects new PDF uploads for public-facing content",
      ]} />
      <H2>Drupal (Common in State Agencies)</H2>
      <P>Drupal's content type system gives agencies more structural control than WordPress, but also more rope to hang themselves with. Custom content types that store documents as file uploads are widespread and almost always inaccessible.</P>
      <H3>Recommended workflow</H3>
      <OL items={[
        "Audit all content types that include file attachment fields",
        "For each content type, add a 'Document Body' long text field as an alternative to file uploads",
        "Update editorial guidelines to require the Document Body field for new entries",
        "Use Drupal's built-in accessibility checker (part of the CKEditor module) before publishing",
        "For existing PDF-heavy content types, implement an AccessPortal widget on the file field template",
      ]} />
      <H2>Granicus (GovDelivery / GovSites)</H2>
      <P>Granicus is specifically built for government and has accessibility features built into its publishing workflow. However, those features only help with content created inside the platform — historical PDF repositories remain unaddressed.</P>
      <UL items={[
        "Enable Granicus's built-in accessibility checker — it's off by default in older installations",
        "Use GovDelivery's template system to enforce heading hierarchy",
        "For document libraries, embed an accessible HTML viewer rather than linking directly to PDFs",
        "Work with your Granicus account manager to audit existing content against WCAG 2.1 AA",
      ]} />
      <H2>The Universal Fix: Intercept at the Link Level</H2>
      <P>Regardless of your CMS, the most practical immediate solution is to intercept PDF links at the browser level and serve an accessible HTML version. A JavaScript widget that detects PDF link clicks and opens a WCAG-compliant HTML modal works with every CMS without requiring any workflow changes from content editors.</P>
      <P>This is what AccessPortal does: one script tag in your site's <Code>&lt;head&gt;</Code> intercepts every PDF click and converts it to accessible HTML on the fly. Content editors keep their existing workflows; users get accessible content.</P>
    </article>
  );
}

// ─── Post 6 ───────────────────────────────────────────────────────────────────
export function Post_ScannedPdfs() {
  return (
    <article className="bp-article">
      <H2>The Scanned PDF Problem Is Bigger Than You Think</H2>
      <P>When government agencies inventory their PDF accessibility issues, they often find that the hardest category to address isn't complex documents with tables and charts — it's the mountains of scanned PDFs that represent decades of record-keeping before digital workflows became standard.</P>
      <P>A scanned PDF is essentially a photograph. Without OCR (Optical Character Recognition), it contains no text data whatsoever — just pixels. A screen reader presented with a scanned PDF without OCR hears nothing. Not garbled text. Not an error message. Silence.</P>
      <H2>How OCR Works (and Why It's Not Enough Alone)</H2>
      <P>OCR converts the pixel image of text back into machine-readable characters. Modern OCR is remarkably accurate for clean, typed documents — typically 98–99% accuracy on standard fonts at normal resolution. But accuracy alone doesn't equal accessibility. After OCR, you still need:</P>
      <UL items={[
        <><B>Reading order detection</B> — Columns, sidebars, headers, and footers must be identified and ordered correctly. OCR reads left-to-right, top-to-bottom by default, which produces nonsense for multi-column layouts.</>,
        <><B>Heading detection</B> — The OCR output is flat text. Identifying what was a heading based on font size and position requires a separate structural analysis step.</>,
        <><B>Table detection</B> — Tables require identifying cell boundaries, spanning cells, and header rows from visual layout cues that OCR doesn't preserve.</>,
        <><B>Image description</B> — Photographs, diagrams, and charts in scanned documents require alt text that OCR cannot generate. This is the one step that still requires human review.</>,
      ]} />
      <H2>Prioritizing Your Scanned PDF Backlog</H2>
      <OL items={[
        <><B>High priority:</B> Documents actively linked from your website, required for current programs or services, or frequently requested through public records requests.</>,
        <><B>Medium priority:</B> Documents from the past 5–10 years that may still be referenced, meeting minutes for elected bodies, financial reports.</>,
        <><B>Low priority / archive:</B> Documents that haven't been accessed in years, historical records kept purely for archival purposes, and content with genuine archival exceptions under the rule.</>,
      ]} />
    </article>
  );
}

// ─── Post 7 ───────────────────────────────────────────────────────────────────
export function Post_UniversityPlaybook() {
  return (
    <article className="bp-article">
      <H2>Why Universities Are the Hardest Case</H2>
      <P>Universities face a PDF accessibility challenge that's categorically different from municipal governments. A city website might have thousands of PDFs. A large university has <Em>millions</Em>: course syllabi, research papers, administrative forms, library resources, faculty publications, student handbooks, accreditation reports, athletic schedules, dining menus, and on and on.</P>
      <H2>The Four Document Categories and How to Handle Each</H2>
      <H3>1. Student-facing course materials (highest priority)</H3>
      <P>Syllabi, assignment sheets, reading lists, and supplementary course materials are specifically called out in the DOJ rule. Password-protected course content in your LMS is not exempt — the final 2024 rule removed exemptions for course materials that were present in earlier drafts.</P>
      <Callout type="warning">
        <B>Action required:</B> Work with your LMS vendor (Canvas, Blackboard, Moodle) to enable accessibility checking for all uploaded files. Require faculty to use the built-in document creation tools rather than uploading external PDFs wherever possible.
      </Callout>
      <H3>2. Administrative forms and processes</H3>
      <P>Registration forms, financial aid documents, withdrawal requests, accommodation request forms — these are high-stakes for students with disabilities because they're the exact documents that students with disabilities are most likely to need. Inaccessible accommodation request forms are the compliance failure that generates the most complaints.</P>
      <H3>3. Research publications and faculty content</H3>
      <P>This is where universities often want to claim archival or third-party exemptions, and sometimes those claims are valid. A faculty member's personal research publication hosted on an external platform for which the university has no editorial control may fall outside the rule.</P>
      <H3>4. Historical archive (may qualify for exception)</H3>
      <P>Genuinely archival content — historical records, digitized special collections, legacy institutional documents not linked from active program pages — may qualify for the archival exception. Document your rationale carefully and review it with legal counsel.</P>
      <H2>The Practical 90-Day Plan</H2>
      <OL items={[
        <><B>Days 1–14:</B> Audit your main university website and top 10 linked department sites. Identify all PDF links using an automated crawler. Categorize by priority tier.</>,
        <><B>Days 15–30:</B> Deploy an accessibility widget to intercept PDF clicks across your main web properties. This immediately addresses the user experience for the highest-traffic documents while the deeper remediation work continues.</>,
        <><B>Days 31–60:</B> Convert high-priority student-facing documents to HTML. Focus on syllabi templates, common administrative forms, and anything linked from your enrollment and financial aid pages.</>,
        <><B>Days 61–90:</B> Establish editorial guidelines and training. No new PDFs on student-facing pages. Require accessibility review before publishing. Designate a web accessibility coordinator as required by the rule.</>,
      ]} />
      <StatRow stats={[
        { num: "4,000+",  label: "U.S. colleges and universities affected" },
        { num: "~$480K",  label: "Average cost of manual PDF remediation for a mid-size university" },
        { num: "90 days", label: "Target time to achieve baseline compliance" },
        { num: "1 line",  label: "Of code needed to deploy an accessibility widget" },
      ]} />
    </article>
  );
}

// ─── Master post registry ─────────────────────────────────────────────────────
export const BLOG_POSTS = [
  {
    slug:        "html-vs-pdf-accessibility",
    title:       "Why HTML Beats PDF Every Time for Government Accessibility",
    date:        "April 10, 2026",
    dateIso:     "2026-04-10",
    readTime:    "6 min read",
    excerpt:     "PDFs were designed for print. Government websites aren't print. Here's the full technical and legal case for switching to accessible HTML.",
    category:    "Accessibility",
    description: "A technical and legal breakdown of why HTML outperforms PDF for WCAG 2.1 AA compliance on government and university websites.",
    component:   Post_HtmlVsPdf,
  },
  {
    slug:        "screen-reader-pdf-vs-html",
    title:       "I Tested 50 Government PDFs with a Screen Reader. Here's What Happened.",
    date:        "March 28, 2026",
    dateIso:     "2026-03-28",
    readTime:    "8 min read",
    excerpt:     "Using NVDA and JAWS, we tested 50 real government PDF documents against their HTML equivalents. The results were stark.",
    category:    "Research",
    description: "Real screen reader testing data comparing 50 government PDFs against their accessible HTML equivalents using NVDA and JAWS.",
    component:   Post_ScreenReaderTest,
  },
  {
    slug:        "wcag-21-explained-for-government",
    title:       "WCAG 2.1 AA Explained: What It Actually Requires for Government Documents",
    date:        "March 15, 2026",
    dateIso:     "2026-03-15",
    readTime:    "9 min read",
    excerpt:     "Most government IT teams know they need WCAG 2.1 AA compliance. Far fewer know exactly what that requires for documents, PDFs, and web content.",
    category:    "Compliance",
    description: "Plain-English breakdown of WCAG 2.1 Level AA criteria that most commonly fail in government PDFs and websites, with practical testing guidance.",
    component:   Post_WcagExplained,
  },
  {
    slug:        "doj-ada-title-ii-deadlines-2026",
    title:       "DOJ ADA Title II Deadlines: Everything Government Entities Need to Know",
    date:        "March 1, 2026",
    dateIso:     "2026-03-01",
    readTime:    "7 min read",
    excerpt:     "The DOJ extended the deadline — but enforcement hasn't paused. Here's the complete breakdown of who must comply, by when, and what's actually covered.",
    category:    "Compliance",
    description: "Complete guide to DOJ ADA Title II digital accessibility deadlines for government and educational entities, including what's covered and what's exempt.",
    component:   Post_DoJDeadlines,
  },
  {
    slug:        "cms-pdf-accessibility-workflow",
    title:       "How to Build an Accessible Document Workflow in WordPress, Drupal, and Granicus",
    date:        "February 18, 2026",
    dateIso:     "2026-02-18",
    readTime:    "10 min read",
    excerpt:     "Platform-specific guidance for the four CMS systems most commonly used by government agencies — with practical workflows your content team can actually follow.",
    category:    "Implementation",
    description: "Step-by-step accessible document publishing workflows for WordPress, Drupal, Granicus, and OpenGov — the most common government CMS platforms.",
    component:   Post_CMSIntegration,
  },
  {
    slug:        "scanned-pdf-ocr-accessibility",
    title:       "Scanned PDFs and OCR: The Hidden Accessibility Crisis in Government Archives",
    date:        "February 5, 2026",
    dateIso:     "2026-02-05",
    readTime:    "8 min read",
    excerpt:     "Millions of government documents exist only as scanned images. Here's what OCR actually does, what it misses, and how to prioritize your remediation backlog.",
    category:    "Research",
    description: "Technical guide to OCR, scanned PDF accessibility, and how to triage and remediate government document archives for ADA Title II compliance.",
    component:   Post_ScannedPdfs,
  },
  {
    slug:        "university-pdf-accessibility-playbook",
    title:       "The University PDF Accessibility Playbook: A 90-Day Compliance Plan",
    date:        "January 22, 2026",
    dateIso:     "2026-01-22",
    readTime:    "11 min read",
    excerpt:     "Universities face a uniquely complex PDF accessibility challenge. Here's a practical 90-day plan for achieving baseline ADA Title II compliance across your institution.",
    category:    "Higher Ed",
    description: "A 90-day practical compliance plan for university PDF accessibility under ADA Title II, covering course materials, admin forms, research content, and archives.",
    component:   Post_UniversityPlaybook,
  },
];

// ─── Blog post CSS ────────────────────────────────────────────────────────────
export const BLOG_POST_CSS = `
.bp-article { max-width: 720px; }
.bp-h2 { font-size: 1.35rem; font-weight: 700; color: var(--navy); margin: 2.2rem 0 .7rem; letter-spacing: -.02em; line-height: 1.3; }
.bp-h3 { font-size: 1.05rem; font-weight: 700; color: var(--navy); margin: 1.5rem 0 .5rem; }
.bp-p  { font-size: .94rem; color: var(--muted); line-height: 1.85; margin: 0 0 1.1rem; }
.bp-ul, .bp-ol { padding-left: 1.4rem; margin: 0 0 1.1rem; display: flex; flex-direction: column; gap: .55rem; }
.bp-ul li, .bp-ol li { font-size: .91rem; color: var(--muted); line-height: 1.75; }
.bp-code { font-family: 'IBM Plex Mono', monospace; font-size: .82em; background: rgba(24,80,160,.07); padding: 2px 6px; border-radius: 4px; color: #1035a0; }
.bp-callout { border-radius: 8px; padding: 1rem 1.25rem; margin: 1.25rem 0; font-size: .88rem; line-height: 1.75; }
.bp-callout--info    { background: #e8f0fe; border-left: 4px solid #1850a0; color: #1a367a; }
.bp-callout--warning { background: #fff7ed; border-left: 4px solid #f5a623; color: #7c4a00; }
.bp-callout--success { background: #d1fae5; border-left: 4px solid #059669; color: #065f46; }
.bp-stat-row { display: flex; flex-wrap: wrap; gap: 1rem; margin: 1.5rem 0; }
.bp-stat { flex: 1; min-width: 130px; background: var(--off); border: 1px solid var(--border); border-radius: 10px; padding: .9rem 1.1rem; }
.bp-stat__n { display: block; font-size: 1.4rem; font-weight: 800; color: var(--blue); letter-spacing: -.02em; }
.bp-stat__l { display: block; font-size: .72rem; color: var(--muted); margin-top: 2px; line-height: 1.4; }
`;

// ─── Global site CSS ──────────────────────────────────────────────────────────
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&family=IBM+Plex+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:      #0b1f35;
  --navy-mid:  #133050;
  --blue:      #1850a0;
  --blue-lt:   #dceafc;
  --blue-dk:   #103578;
  --muted:     #4a5f76;
  --off:       #f4f7fb;
  --off2:      #eef2f8;
  --border:    #d6e0ee;
  --red:       #c0392b;
  --font:      'IBM Plex Sans', system-ui, -apple-system, sans-serif;
  --mono:      'IBM Plex Mono', 'Fira Code', monospace;
}

html { scroll-behavior: smooth; }

body {
  font-family: var(--font);
  color: var(--navy);
  background: #fff;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

h1, h2, h3, h4 {
  font-family: var(--font);
  font-weight: 800;
  letter-spacing: -.02em;
  line-height: 1.2;
}

a { color: inherit; text-decoration: none; }
button { font-family: var(--font); cursor: pointer; }
ul, ol { list-style: none; }

/* ── Fade animation (IntersectionObserver) ── */
.fade { opacity: 0; transform: translateY(18px); transition: opacity .55s ease, transform .55s ease; }
.fade.on { opacity: 1; transform: none; }

/* ── Nav ── */
.nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  height: 64px;
  background: rgba(11,31,53,0.97);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255,255,255,.08);
}
.nav-inner {
  max-width: 1160px; margin: 0 auto;
  height: 100%; padding: 0 5%;
  display: flex; align-items: center; justify-content: space-between;
}
.logo {
  display: flex; align-items: center; gap: 9px;
  font-size: .9rem; font-weight: 800; color: #fff;
  letter-spacing: -.01em; cursor: pointer;
}
.logo-icon {
  width: 30px; height: 30px;
  background: var(--blue);
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.nav-links {
  display: flex; align-items: center; gap: .25rem;
  list-style: none;
}
.nav-links li a {
  display: inline-block;
  padding: 6px 13px;
  font-size: .8rem; font-weight: 600; color: rgba(255,255,255,.7);
  border-radius: 6px;
  cursor: pointer;
  transition: color .15s, background .15s;
}
.nav-links li a:hover { color: #fff; background: rgba(255,255,255,.08); }
.nav-cta {
  background: var(--blue) !important;
  color: #fff !important;
  border-radius: 6px;
  padding: 7px 15px !important;
  font-weight: 700 !important;
  transition: background .15s !important;
}
.nav-cta:hover { background: #2563c4 !important; }

/* ── Buttons ── */
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center;
  height: 48px; padding: 0 28px;
  background: var(--blue);
  color: #fff;
  font-size: .88rem; font-weight: 700; letter-spacing: .01em;
  border: none; border-radius: 8px;
  cursor: pointer;
  transition: background .15s, box-shadow .15s, transform .1s;
  box-shadow: 0 2px 12px rgba(24,80,160,.35);
}
.btn-primary:hover {
  background: #2563c4;
  box-shadow: 0 4px 20px rgba(24,80,160,.45);
  transform: translateY(-1px);
}
.btn-ghost {
  display: inline-flex; align-items: center; justify-content: center;
  height: 48px; padding: 0 26px;
  background: rgba(255,255,255,.1);
  color: #fff;
  font-size: .88rem; font-weight: 600;
  border: 1.5px solid rgba(255,255,255,.25);
  border-radius: 8px;
  cursor: pointer;
  transition: background .15s, border-color .15s;
}
.btn-ghost:hover { background: rgba(255,255,255,.18); border-color: rgba(255,255,255,.4); }
.btn-blue-ghost {
  display: inline-flex; align-items: center; justify-content: center;
  height: 44px; padding: 0 22px;
  background: none;
  color: var(--blue);
  font-size: .85rem; font-weight: 700;
  border: 1.5px solid var(--blue);
  border-radius: 8px;
  cursor: pointer;
  transition: background .15s;
}
.btn-blue-ghost:hover { background: var(--blue-lt); }
.btn-group { display: flex; gap: .75rem; flex-wrap: wrap; }

/* ── Hero ── */
.hero {
  position: relative;
  background: linear-gradient(145deg, var(--navy) 0%, #1a3a6b 55%, #0d2645 100%);
  padding: 8rem 5% 6rem;
  overflow: hidden;
}
.hero-bg-dots {
  position: absolute; inset: 0;
  background-image: radial-gradient(rgba(255,255,255,.04) 1px, transparent 1px);
  background-size: 28px 28px;
  pointer-events: none;
}
.hero-mesh {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 80% 60% at 70% 50%, rgba(24,80,160,.35) 0%, transparent 70%);
  pointer-events: none;
}
.hero-inner {
  max-width: 1160px; margin: 0 auto;
  display: flex; align-items: center; gap: 4rem;
  position: relative; z-index: 1;
}
.hero-content { flex: 1; min-width: 0; }
.hero-badge {
  display: inline-flex; align-items: center; gap: 7px;
  background: rgba(245,166,35,.15);
  border: 1px solid rgba(245,166,35,.35);
  color: #f9c84a;
  font-size: .72rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  padding: 5px 13px; border-radius: 100px;
  margin-bottom: 1.25rem;
}
.badge-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #f5a623;
  box-shadow: 0 0 6px #f5a623;
  animation: pulse-badge 2s ease-in-out infinite;
  display: inline-block;
}
@keyframes pulse-badge {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .5; transform: scale(.8); }
}
.hero h1 {
  font-size: clamp(2.1rem, 4.5vw, 3.4rem);
  color: #fff;
  margin-bottom: 1.25rem;
  line-height: 1.12;
}
.hero h1 em { color: #7ec0ff; font-style: normal; }
.hero-sub {
  font-size: 1rem; color: rgba(255,255,255,.65);
  line-height: 1.75; max-width: 520px;
  margin-bottom: 2rem; font-weight: 300;
}
.hero-ctas { display: flex; gap: .875rem; flex-wrap: wrap; margin-bottom: 2rem; }
.social-proof { display: flex; align-items: center; gap: 12px; }
.avatars { display: flex; }
.av {
  width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: .65rem; font-weight: 800; color: #fff;
  border: 2px solid var(--navy);
  margin-left: -8px;
  flex-shrink: 0;
}
.av:first-child { margin-left: 0; }
.av1 { background: #2563c4; }
.av2 { background: #059669; }
.av3 { background: #7c3aed; }
.av4 { background: #dc2626; }
.av5 { background: #d97706; }
.proof-text { font-size: .78rem; color: rgba(255,255,255,.65); line-height: 1.5; }
.proof-text strong { color: #fff; }
.hero-visual { flex: 0 0 auto; width: min(480px, 42vw); }
.hero-svg-wrap { width: 100%; }
@media (max-width: 860px) {
  .hero-visual { display: none; }
  .hero-inner { justify-content: center; }
  .hero-content { text-align: center; }
  .hero-ctas { justify-content: center; }
  .social-proof { justify-content: center; }
}

/* ── Trust bar ── */
.trust-bar {
  background: var(--off2);
  border-bottom: 1px solid var(--border);
  padding: 1.1rem 5%;
}
.trust-inner {
  max-width: 1160px; margin: 0 auto;
  display: flex; align-items: center; justify-content: center;
  flex-wrap: wrap; gap: 0;
}
.trust-item {
  display: flex; align-items: center; gap: 7px;
  font-size: .78rem; font-weight: 600; color: var(--navy);
}
.trust-icon { width: 16px; height: 16px; flex-shrink: 0; }
.trust-divider { width: 1px; height: 24px; background: var(--border); margin: 0 2.5rem; flex-shrink: 0; }

/* ── Sections ── */
.section { padding: 5.5rem 5%; }
.section-inner { max-width: 1160px; margin: 0 auto; }
.section-label {
  display: flex; align-items: center; gap: 8px;
  font-size: .65rem; font-weight: 700; letter-spacing: .18em; text-transform: uppercase;
  color: var(--blue); margin-bottom: .75rem;
}
.section-label::before {
  content: '';
  width: 18px; height: 2px;
  background: var(--blue);
  display: inline-block;
  border-radius: 1px;
}
.section-title {
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  color: var(--navy);
  margin-bottom: 1rem;
  line-height: 1.18;
}
.section-sub {
  font-size: .94rem; color: var(--muted);
  line-height: 1.8; max-width: 560px;
  margin-bottom: 2.5rem; font-weight: 300;
}

/* ── Steps ── */
.steps {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.5rem;
  margin: 2.5rem 0;
}
.steps-3 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
  margin: 2.5rem 0;
}
.step {
  background: var(--off); border: 1px solid var(--border);
  border-radius: 12px; padding: 1.5rem;
  transition: border-color .2s, box-shadow .2s;
}
.step:hover { border-color: var(--blue); box-shadow: 0 4px 20px rgba(24,80,160,.08); }
.step-num {
  font-size: .58rem; font-weight: 800; letter-spacing: .2em;
  text-transform: uppercase; color: var(--blue);
  margin-bottom: .625rem;
}
.step-ico {
  width: 38px; height: 38px;
  background: rgba(24,80,160,.08);
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: .875rem;
}
.step h3 {
  font-size: .9rem; font-weight: 700; color: var(--navy);
  margin-bottom: .5rem;
}
.step p { font-size: .82rem; color: var(--muted); line-height: 1.7; }

/* ── Code card ── */
.code-card {
  background: #0d1b2a; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(255,255,255,.06);
  margin: 2rem 0;
  max-width: 680px;
}
.code-top {
  display: flex; align-items: center; gap: 6px;
  background: #132333; padding: 10px 16px;
  border-bottom: 1px solid rgba(255,255,255,.06);
}
.code-dot { width: 11px; height: 11px; border-radius: 50%; display: inline-block; }
.cd-r { background: #ff5f57; }
.cd-y { background: #febc2e; }
.cd-g { background: #28c840; }
.code-file {
  font-size: .68rem; color: rgba(255,255,255,.3);
  font-family: var(--mono); margin-left: 4px;
}
.code-body {
  padding: 1.25rem 1.5rem;
  font-family: var(--mono); font-size: .78rem;
  line-height: 2; color: #ddeaf6;
}
.cm { color: rgba(255,255,255,.3); }
.ck { color: #7bbcf8; }
.cv { color: #a8d8ff; }
.cs { color: #a8f3c0; }

/* ── Stats row (home) ── */
.stat-row {
  display: flex; flex-wrap: wrap; gap: 1.5rem;
  margin: 2rem 0;
}
.stat-row > div { flex: 1; min-width: 140px; }
.stat-num {
  font-size: 2rem; font-weight: 800; color: var(--navy);
  letter-spacing: -.03em; line-height: 1;
  margin-bottom: 4px;
}
.stat-label { font-size: .76rem; color: var(--muted); line-height: 1.4; }

/* ── Urgency card ── */
.urgency-card {
  display: flex; align-items: flex-start; gap: 1.25rem;
  background: #fff7ed;
  border: 1.5px solid #fbd38d;
  border-radius: 12px; padding: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.urg-icon {
  width: 44px; height: 44px; flex-shrink: 0;
  background: #fef3c7; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.urg-title {
  font-size: .95rem; font-weight: 700; color: #7c4a00;
  margin-bottom: .35rem;
}
.urg-sub { font-size: .83rem; color: #9a6000; line-height: 1.7; }

/* ── Portal pricing strip ── */
.portal-pricing-strip {
  display: flex; align-items: flex-start; gap: 2rem;
  background: var(--navy); border-radius: 14px; padding: 2rem;
  margin-top: 3rem; flex-wrap: wrap;
}
.pps-left { flex: 1; min-width: 220px; }
.pps-left h3 {
  font-size: 1rem; font-weight: 700; color: #fff;
  margin-bottom: .5rem;
}
.pps-left p { font-size: .82rem; color: rgba(255,255,255,.55); line-height: 1.6; }
.pps-tiers { display: flex; gap: 1rem; flex-wrap: wrap; }
.pps-tier {
  background: rgba(255,255,255,.07);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 10px; padding: 1rem 1.25rem;
  min-width: 140px;
}
.pps-vol { font-size: .72rem; color: rgba(255,255,255,.5); margin-bottom: 4px; }
.pps-price {
  font-size: 1.5rem; font-weight: 800; color: #fff;
  letter-spacing: -.03em; margin-bottom: 2px;
}
.pps-note { font-size: .68rem; color: rgba(255,255,255,.4); }

/* ── Pricing ── */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}
.p-card {
  background: #fff; border: 1.5px solid var(--border);
  border-radius: 14px; padding: 2rem;
  position: relative;
  transition: border-color .2s, box-shadow .2s;
}
.p-card:hover { border-color: var(--blue); box-shadow: 0 6px 30px rgba(24,80,160,.1); }
.p-card.featured {
  border-color: var(--blue);
  box-shadow: 0 8px 40px rgba(24,80,160,.15);
}
.pop-badge {
  position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
  background: var(--blue); color: #fff;
  font-size: .65rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
  padding: 4px 14px; border-radius: 100px;
  white-space: nowrap;
}
.plan-tag {
  font-size: .65rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase;
  color: var(--muted); margin-bottom: .75rem;
}
.plan-price {
  font-size: 2.6rem; font-weight: 800; color: var(--navy);
  letter-spacing: -.04em; line-height: 1; margin-bottom: 2px;
}
.plan-price sup { font-size: 1.1rem; vertical-align: super; line-height: 0; }
.plan-mo { font-size: .75rem; color: var(--muted); margin-bottom: 1rem; }
.plan-desc { font-size: .84rem; color: var(--muted); line-height: 1.6; margin-bottom: 1.5rem; min-height: 50px; }
.feat-list {
  list-style: none; margin-bottom: 1.75rem;
  display: flex; flex-direction: column; gap: .6rem;
}
.feat-list li {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: .83rem; color: var(--muted); line-height: 1.5;
}
.fc {
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--blue-lt); display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; margin-top: 1px;
}

/* ── Testimonials ── */
.testi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;
}
.t-card {
  background: #fff; border: 1px solid var(--border);
  border-radius: 14px; padding: 1.75rem;
}
.t-stars { color: #f5a623; font-size: .95rem; margin-bottom: .875rem; letter-spacing: 2px; }
.t-quote {
  font-size: .88rem; color: var(--muted); line-height: 1.8;
  margin-bottom: 1.25rem; font-style: normal;
}
.t-author { display: flex; align-items: center; gap: 11px; }
.t-av {
  width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: .75rem; font-weight: 800; color: #fff; flex-shrink: 0;
}
.t-name { font-size: .84rem; font-weight: 700; color: var(--navy); }
.t-role { font-size: .73rem; color: var(--muted); margin-top: 1px; }

/* ── Blog grid ── */
.blog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}
.blog-card {
  background: #fff; border: 1.5px solid var(--border);
  border-radius: 12px; overflow: hidden;
  cursor: pointer;
  display: flex; flex-direction: column;
  transition: border-color .18s, box-shadow .18s, transform .18s;
}
.blog-card:hover {
  border-color: var(--blue);
  box-shadow: 0 6px 24px rgba(24,80,160,.1);
  transform: translateY(-2px);
}
.blog-card-top { padding: 1.1rem 1.25rem .625rem; }
.blog-cat {
  display: inline-block;
  font-size: .65rem; font-weight: 700; letter-spacing: .07em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 100px;
}
.blog-card-body { padding: 0 1.25rem 1rem; flex: 1; }
.blog-card-title { font-size: 1rem; font-weight: 700; color: var(--navy); margin: 0 0 .5rem; line-height: 1.35; }
.blog-card-body p { font-size: .81rem; color: var(--muted); line-height: 1.7; }
.blog-meta {
  display: flex; gap: 1rem; margin-top: .875rem;
  font-size: .73rem; color: var(--muted); flex-wrap: wrap;
}
.blog-card-footer { padding: .75rem 1.25rem; border-top: 1px solid var(--border); }
.blog-read-more { font-size: .78rem; font-weight: 700; color: var(--blue); }

/* ── Blog hero bar ── */
.blog-hero-bar {
  background: linear-gradient(135deg, var(--navy), var(--navy-mid));
  padding: 7rem 5% 3.5rem;
  margin-top: 64px;
}
.blog-hero-bar-inner { max-width: 780px; margin: 0 auto; }
.back-link {
  display: inline-block;
  font-size: .78rem; font-weight: 600; color: rgba(255,255,255,.55);
  margin-bottom: 1.25rem; cursor: pointer;
  transition: color .15s;
  background: none; border: none; padding: 0;
}
.back-link:hover { color: #fff; }
.post-title {
  font-size: clamp(1.5rem, 3.5vw, 2.4rem);
  color: #fff; margin-bottom: 1rem; line-height: 1.2;
}
.post-meta {
  display: flex; align-items: center; gap: .875rem;
  font-size: .78rem; color: rgba(255,255,255,.55); flex-wrap: wrap;
}
.post-meta time, .post-meta span { color: rgba(255,255,255,.55); }

/* ── Blog post body ── */
.blog-post { padding: 3.5rem 5%; }
.blog-post-inner { max-width: 780px; margin: 0 auto; }

/* ── CTA section ── */
.cta-section {
  position: relative;
  background: linear-gradient(135deg, var(--navy), #1a3a6b);
  padding: 6rem 5%; text-align: center; overflow: hidden;
}
.cta-mesh {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 80% at 50% 50%, rgba(24,80,160,.4) 0%, transparent 70%);
  pointer-events: none;
}
.cta-inner { position: relative; z-index: 1; max-width: 600px; margin: 0 auto; }
.cta-inner h2 {
  font-size: clamp(1.7rem, 3.5vw, 2.6rem);
  color: #fff; margin-bottom: 1rem;
}
.cta-inner p { font-size: .94rem; color: rgba(255,255,255,.6); margin-bottom: 2rem; line-height: 1.7; }

/* ── Footer ── */
footer {
  background: var(--navy);
  padding: 3rem 5%;
  border-top: 1px solid rgba(255,255,255,.06);
}
.footer-inner {
  max-width: 1160px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: center; gap: 1.25rem;
}
.footer-logo {
  display: flex; align-items: center; gap: 9px;
  font-size: .88rem; font-weight: 800; color: #fff;
}
.footer-logo-icon {
  width: 28px; height: 28px; background: var(--blue); border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
}
.footer-links {
  display: flex; align-items: center; flex-wrap: wrap; gap: .25rem;
  list-style: none; justify-content: center;
}
.footer-links li a {
  display: inline-block;
  padding: 4px 10px; font-size: .76rem; color: rgba(255,255,255,.45);
  cursor: pointer; transition: color .15s; border-radius: 4px;
}
.footer-links li a:hover { color: rgba(255,255,255,.8); }
.footer-copy { font-size: .72rem; color: rgba(255,255,255,.25); }

/* ── Checkout ── */
.checkout-page {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  padding: 2rem 5%; background: var(--off);
}
.checkout-inner {
  background: #fff; border: 1.5px solid var(--border);
  border-radius: 16px; padding: 3rem 2.5rem;
  max-width: 500px; width: 100%;
  text-align: center;
  box-shadow: 0 8px 40px rgba(11,31,53,.08);
}
.checkout-inner h1 { font-size: 1.75rem; color: var(--navy); margin-bottom: .75rem; }
.checkout-inner h2 { font-size: 1.4rem; color: var(--navy); margin-bottom: .75rem; }
.checkout-inner p { font-size: .88rem; color: var(--muted); line-height: 1.7; margin-bottom: 1rem; }
.plan-note {
  background: var(--blue-lt); border: 1px solid #b3d0f5;
  border-radius: 8px; padding: .875rem 1.1rem;
  font-size: .83rem; color: var(--blue-dk); line-height: 1.65;
  margin-bottom: 1.75rem; text-align: left;
}
.pro-features {
  background: var(--off); border: 1px solid var(--border);
  border-radius: 10px; padding: 1.1rem 1.25rem;
  margin-bottom: 1.75rem; text-align: left;
}
.pro-features-label {
  font-size: .6rem; font-weight: 700; letter-spacing: .14em; text-transform: uppercase;
  color: var(--muted); margin-bottom: .75rem;
}
.pro-feat-item {
  display: flex; align-items: center; gap: 9px;
  font-size: .82rem; color: var(--muted); padding: 4px 0; line-height: 1.5;
}
.revoke-btn {
  display: inline-block; margin-top: 1.25rem;
  font-size: .73rem; color: var(--muted);
  background: none; border: none; cursor: pointer;
  text-decoration: underline; text-underline-offset: 2px;
  transition: color .15s;
}
.revoke-btn:hover { color: var(--red); }

@media (max-width: 640px) {
  .section { padding: 4rem 4%; }
  .pricing-grid { grid-template-columns: 1fr; }
  .testi-grid { grid-template-columns: 1fr; }
  .blog-grid { grid-template-columns: 1fr; }
  .trust-inner { gap: 1rem; }
  .trust-divider { display: none; }
  .steps, .steps-3 { grid-template-columns: 1fr; }
  .portal-pricing-strip { flex-direction: column; }
  .pps-tiers { flex-direction: column; }
  .hero { padding: 7rem 4% 4rem; }
}
`;

// ─── Stripe config ────────────────────────────────────────────────────────────
const STRIPE = {
  links: {
    daypass: process.env.REACT_APP_STRIPE_LINK_DAYPASS,
    pro:     process.env.REACT_APP_STRIPE_LINK_PRO,
  },
};

// ─── Token generators ─────────────────────────────────────────────────────────
function makeToken(domain, expires, secret) {
  if (!domain || !expires || !secret) return '';
  try { return 'tok_' + btoa(domain) + '_' + btoa(expires) + '_' + secret; }
  catch (e) { return ''; }
}

function makeOpenToken(expires, secret) {
  if (!expires || !secret) return '';
  try { return 'tok_open_' + btoa(expires) + '_' + secret; }
  catch (e) { return ''; }
}

// ─── SEO component ────────────────────────────────────────────────────────────
function SEO({ title, description, canonical, type = "website", noindex = false }) {
  const siteName  = "AccessPortal";
  const fullTitle = title
    ? `${title} | ${siteName}`
    : `${siteName} — ADA PDF Accessibility for Government`;
  const baseUrl      = "https://accessportal.io";
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : baseUrl;
  const desc = description || "One script tag makes every PDF on your government site instantly accessible and WCAG 2.1 AA compliant.";
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description"          content={desc} />
      <link rel="canonical"             href={canonicalUrl} />
      {noindex && <meta name="robots"   content="noindex,nofollow" />}
      <meta property="og:title"         content={fullTitle} />
      <meta property="og:description"   content={desc} />
      <meta property="og:type"          content={type} />
      <meta property="og:url"           content={canonicalUrl} />
      <meta property="og:site_name"     content={siteName} />
      <meta name="twitter:card"         content="summary_large_image" />
      <meta name="twitter:title"        content={fullTitle} />
      <meta name="twitter:description"  content={desc} />
    </>
  );
}

// ─── Article JSON-LD ─────────────────────────────────────────────────────────
function ArticleJsonLd({ post }) {
  const ld = JSON.stringify({
    "@context":      "https://schema.org",
    "@type":         "Article",
    "headline":      post.title,
    "description":   post.description || post.excerpt,
    "datePublished": post.dateIso || post.date,
    "publisher":     { "@type": "Organization", "name": "AccessPortal", "url": "https://accessportal.io" },
  });
  return <script type="application/ld+json" suppressHydrationWarning>{ld}</script>;
}

// ─── Router ───────────────────────────────────────────────────────────────────
const RouterCtx = createContext(null);
const normalise  = p => (p.length > 1 ? p.replace(/\/$/, "") : p);
const scrollTop  = () => window.scrollTo(0, 0);

function RouterProvider({ children }) {
  const [pathname, setPathname] = useState(normalise(window.location.pathname));
  useEffect(() => {
    const onPop = () => { setPathname(normalise(window.location.pathname)); scrollTop(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = useCallback(path => {
    const norm = normalise(path);
    window.history.pushState(null, "", norm);
    setPathname(norm);
    scrollTop();
  }, []);
  return <RouterCtx.Provider value={{ pathname, navigate }}>{children}</RouterCtx.Provider>;
}

function usePathname() { return useContext(RouterCtx)?.pathname ?? "/"; }
function useNavigate() { return useContext(RouterCtx)?.navigate ?? (() => {}); }

const Link = forwardRef(function Link({ href, children, className, style, onClick, ...rest }, ref) {
  const navigate = useNavigate();
  const handleClick = e => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0 ||
        !href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
    e.preventDefault();
    navigate(href);
  };
  return <a ref={ref} href={href} className={className} style={style} onClick={handleClick} {...rest}>{children}</a>;
});

function matchRoute(pattern, pathname) {
  const pp = pattern.split("/"), up = normalise(pathname).split("/");
  if (pp.length !== up.length) return null;
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) params[pp[i].slice(1)] = decodeURIComponent(up[i]);
    else if (pp[i] !== up[i]) return null;
  }
  return { params };
}

function matchRoutes(routes, pathname) {
  for (const route of routes) {
    const m = matchRoute(route.path, normalise(pathname));
    if (m) return { ...route, params: m.params };
  }
  return null;
}

// ─── Access gate ─────────────────────────────────────────────────────────────
const DAYPASS_TTL = 24 * 60 * 60 * 1000;
const PRO_TTL     = 365 * 24 * 60 * 60 * 1000;
const getStorage  = plan => plan === "daypass" ? sessionStorage : localStorage;
const getTTL      = plan => plan === "daypass" ? DAYPASS_TTL : PRO_TTL;

function isValidRef(val) {
  return typeof val === "string" &&
    (val.startsWith("cs_live_") || val.startsWith("cs_test_")) &&
    val.length >= 50;
}

function getFingerprint() {
  try {
    return [
      navigator.userAgent,
      navigator.language,
      `${window.screen.width}x${window.screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.hardwareConcurrency,
    ].join("|");
  } catch { return "fp_unavailable"; }
}

function grantAccess(ref, plan) {
  try {
    const s = getStorage(plan);
    s.setItem("ap_uid",     ref);
    s.setItem("ap_fp",      getFingerprint());
    s.setItem("ap_granted", Date.now().toString());
    s.setItem("ap_plan",    plan);
  } catch {}
}

function checkAccess() {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const uid       = store.getItem("ap_uid");
      const storedFp  = store.getItem("ap_fp");
      const grantedAt = parseInt(store.getItem("ap_granted") || "0");
      const plan      = store.getItem("ap_plan") || "daypass";
      if (!uid || Date.now() - grantedAt > getTTL(plan) || storedFp !== getFingerprint()) continue;
      return { granted: true, plan };
    } catch {}
  }
  return { granted: false, plan: null };
}

function revokeAccess() {
  ["ap_uid", "ap_fp", "ap_granted", "ap_plan"].forEach(k => {
    try { sessionStorage.removeItem(k); } catch {}
    try { localStorage.removeItem(k);   } catch {}
  });
}

// ─── Shared icons ─────────────────────────────────────────────────────────────
const LogoIcon = () => (
  <div className="logo-icon">
    <svg viewBox="0 0 16 16" style={{ width:16, height:16, fill:"none", stroke:"#fff", strokeWidth:2, strokeLinecap:"round" }}>
      <path d="M3 4h10M3 8h10M3 12h6" />
    </svg>
  </div>
);

const CheckIcon = () => (
  <svg viewBox="0 0 10 10" style={{ width:8, height:8, stroke:"var(--blue)", fill:"none", strokeWidth:2.5, strokeLinecap:"round" }}>
    <path d="M2 5l2 2 4-4" />
  </svg>
);

// ─── useFade ──────────────────────────────────────────────────────────────────
function useFade() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("on"); obs.unobserve(e.target); } });
    }, { threshold: 0.1 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
function Nav() {
  const navigate    = useNavigate();
  const { granted } = checkAccess();
  return (
    <nav className="nav">
      <div className="nav-inner">
        <div className="logo" onClick={() => navigate("/")}>
          <LogoIcon /> AccessPortal
        </div>
        <ul className="nav-links">
          <li><a onClick={() => navigate("/#how")}>How it works</a></li>
          <li><a onClick={() => navigate("/#pricing")}>Pricing</a></li>
          <li><a onClick={() => navigate("/#portal")}>Portal</a></li>
          <li><a onClick={() => navigate("/blog")}>Blog</a></li>
          {granted
            ? <li><a onClick={() => navigate("/dashboard")} className="nav-cta">Dashboard ↗</a></li>
            : <li><a onClick={() => navigate("/dashboard")} className="nav-cta">Dashboard ↗</a></li>
          }
        </ul>
      </div>
    </nav>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  const navigate = useNavigate();
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-logo">
          <div className="footer-logo-icon">
            <svg viewBox="0 0 16 16" style={{ width:14, height:14, fill:"none", stroke:"#fff", strokeWidth:2, strokeLinecap:"round" }}>
              <path d="M3 4h10M3 8h10M3 12h6" />
            </svg>
          </div>
          AccessPortal
        </div>
        <ul className="footer-links">
          <li><a href="#">Privacy Policy</a></li>
          <li><a href="#">Terms of Service</a></li>
          <li><a href="#">VPAT</a></li>
          <li><a onClick={() => navigate("/blog")}>Blog</a></li>
          <li><a href="#">Contact Support</a></li>
        </ul>
        <div className="footer-copy">© 2026 AccessPortal. All rights reserved.</div>
      </div>
    </footer>
  );
}

// ─── Dashboard helpers ────────────────────────────────────────────────────────
const STATES_LIST = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming",
];

const getToday    = () => new Date().toISOString().split("T")[0];
const getNextYear = d  => { const x = new Date(d); x.setFullYear(x.getFullYear() + 1); return x.toISOString().split("T")[0]; };

const EMPTY_DATA = {
  name:"", state:"", created:"", expiry:"", color:"#1850a0",
  domain:"", secret:"",
  tokenMode:"domain",
  logo:"", logoAlt:"", lang:"en-US", selector:"#main-content",
  ogImage:"", bypassList:"confidential,draft,internal",
  allowTypes:"minutes,agenda,report,budget", maxSizeMb:15,
  ocr:true, search:true, download:true, copy:true, share:true, remediate:true, dev:false,
};

const BOOL_KEYS   = ["ocr","search","download","copy","share","remediate","dev"];

const SCALAR_KEYS = [
  "name", "state", "created", "expiry", "color",
  "domain", "secret", "tokenMode",
  "logo", "logoAlt", "lang", "selector", "ogImage", "bypassList", "allowTypes", "maxSizeMb",
];

function serializeToFragment(data) {
  const parts = SCALAR_KEYS
    .filter(k => data[k] !== undefined && data[k] !== "")
    .map(k => `${k}=${encodeURIComponent(String(data[k]))}`);
  BOOL_KEYS.forEach(k => parts.push(`${k}=${data[k] ? "1" : "0"}`));
  return parts.join("&");
}

function deserializeFromFragment(hash) {
  if (!hash) return null;
  try {
    const d = { ...EMPTY_DATA };
    hash.split("&").forEach(pair => {
      const eq = pair.indexOf("="); if (eq < 0) return;
      const key = pair.slice(0, eq), val = decodeURIComponent(pair.slice(eq + 1));
      if (BOOL_KEYS.includes(key))  { d[key] = val === "1"; return; }
      if (key === "maxSizeMb")      { d[key] = Number(val); return; }
      if (key in d)                 { d[key] = val; }
    });
    return d;
  } catch { return null; }
}

function useLiveAnnounce() {
  const [msg, setMsg] = useState("");
  const announce = useCallback(t => { setMsg(""); requestAnimationFrame(() => setMsg(t)); }, []);
  return { msg, announce };
}

// ─── Dashboard sub-components ─────────────────────────────────────────────────
function DashToggle({ id, value, onChange, disabled, ariaLabel }) {
  return (
    <label className="dash-tog" aria-label={ariaLabel}>
      <input type="checkbox" id={id} role="switch" checked={value} disabled={!!disabled}
        onChange={e => !disabled && onChange(e.target.checked)} />
      <span className="dash-tog__track" />
      <span className="dash-tog__thumb" />
    </label>
  );
}

function DashCopyBtn({ text, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const { msg, announce }   = useLiveAnnounce();
  const go = () => navigator.clipboard.writeText(text).then(() => {
    setCopied(true); announce("Copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  });
  return (
    <>
      <button type="button"
        className={`dash-btn-copy${copied ? " dash-btn-copy--ok" : ""}`}
        onClick={go} aria-label={copied ? "Copied to clipboard" : label}>
        <span aria-hidden="true">{copied ? "✓" : "⎘"}</span>
        {copied ? "Copied!" : label}
      </button>
      <span className="dash-sr-only" aria-live="polite" aria-atomic="true">{msg}</span>
    </>
  );
}

function DashCodeBlock({ filename, code, id }) {
  const lid = `${id}-lbl`;
  return (
    <div className="dash-codeblock" role="region" aria-labelledby={lid}>
      <div className="dash-codeblock__bar" aria-hidden="true">
        {["#ff5f57","#febc2e","#28c840"].map(c => <span key={c} className="dash-codeblock__dot" style={{ background:c }}/>)}
        {filename && <span className="dash-codeblock__file">{filename}</span>}
      </div>
      <p id={lid} className="dash-sr-only">{filename ? `Code: ${filename}` : "Code sample"}</p>
      <pre className="dash-codeblock__pre" tabIndex={0}
        aria-label={`${filename || "Code"} — scroll or use arrow keys to read`}>{code}</pre>
    </div>
  );
}

function DashField({ label, id, hint, required, children }) {
  return (
    <div className="dash-f">
      <label className="dash-f__lbl" htmlFor={id}>
        {label}
        {required && <><span className="dash-f__req" aria-hidden="true"> *</span><span className="dash-sr-only"> (required)</span></>}
      </label>
      {hint && <span className="dash-f__hint" id={`${id}-hint`}>{hint}</span>}
      {children}
    </div>
  );
}

function DashCard({ title, id, children }) {
  const hid = `${id}-h`;
  return (
    <section className="dash-card" aria-labelledby={hid}>
      <h2 id={hid} className="dash-card__title">{title}</h2>
      {children}
    </section>
  );
}

const DASH_TABS = [
  { id:"config",   label:"Configuration" },
  { id:"upload",   label:"Upload PDF"    },
  { id:"advanced", label:"Advanced"      },
  { id:"preview",  label:"Live Preview"  },
  { id:"embed",    label:"Embed Code"    },
];

function DashTabBar({ active, onChange }) {
  const refs = useRef([]);
  const onKey = (e, i) => {
    let n = i;
    if      (e.key === "ArrowRight") n = (i + 1) % DASH_TABS.length;
    else if (e.key === "ArrowLeft")  n = (i - 1 + DASH_TABS.length) % DASH_TABS.length;
    else if (e.key === "Home")       n = 0;
    else if (e.key === "End")        n = DASH_TABS.length - 1;
    else return;
    e.preventDefault(); onChange(DASH_TABS[n].id); refs.current[n]?.focus();
  };
  return (
    <nav className="dash-tabbar" aria-label="Dashboard sections">
      <div role="tablist" className="dash-tabbar__inner">
        {DASH_TABS.map((t, i) => (
          <button key={t.id} ref={el => refs.current[i] = el}
            role="tab" type="button" id={`dash-tab-${t.id}`}
            aria-selected={active === t.id} aria-controls={`dash-panel-${t.id}`}
            tabIndex={active === t.id ? 0 : -1}
            className={`dash-tabbar__tab${active === t.id ? " dash-tabbar__tab--on" : ""}`}
            onClick={() => onChange(t.id)} onKeyDown={e => onKey(e, i)}>
            {t.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate          = useNavigate();
  // ── PATCH: bypass auth gate — always grant access for development ──
  const access            = { granted: true, plan: 'pro' };
  const mainRef           = useRef(null);
  const { msg, announce } = useLiveAnnounce();

  const [data,    setData]    = useState(EMPTY_DATA);
  const [saved,   setSaved]   = useState(false);
  const [tab,     setTab]     = useState("config");
  const [pdfDrag, setPdfDrag] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);

  // ── PATCH: removed auth redirect useEffect ──

  const loadFromHash = useCallback(() => {
    const h = window.location.hash.slice(1); if (!h) return;
    const p = deserializeFromFragment(h); if (p) setData(p);
  }, []);

  useEffect(() => {
    loadFromHash();
    window.addEventListener("hashchange", loadFromHash);
    return () => window.removeEventListener("hashchange", loadFromHash);
  }, [loadFromHash]);

  useEffect(() => {
    const t = setTimeout(() => {
      const frag = serializeToFragment(data);
      const next = `#${frag}`;
      if (window.location.hash !== next) {
        window.history.replaceState(null, "", next);
        setSaved(true); announce("Saved");
        setTimeout(() => setSaved(false), 1800);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [data]);

  const isLocked  = data.expiry && new Date() > new Date(data.expiry);
  const set       = (k, v) => setData(p => ({ ...p, [k]: v }));
  const isOpenMode = data.tokenMode === "open";

  const handleName = val => {
    if (isLocked) return;
    const u = { name: val };
    if (!data.created) { u.created = getToday(); u.expiry = getNextYear(u.created); }
    setData(p => ({ ...p, ...u }));
  };

  const changeTab = t => { setTab(t); requestAnimationFrame(() => mainRef.current?.focus()); };

  const reset = () => {
    if (!window.confirm("Reset all configuration to defaults?")) return;
    window.history.replaceState(null, "", window.location.pathname);
    setData(EMPTY_DATA); announce("Reset to defaults");
  };

  const handleDzKey = e => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); document.getElementById("dash-pdf-in")?.click(); }
  };

  // ─── Token generation ─────────────────────────────────────────────────────
  const brand   = data.name   || "City of Springfield";
  const expires = data.expiry || getNextYear(getToday());

  const token = isOpenMode
    ? makeOpenToken(expires, data.secret || "your-secret")
    : makeToken(data.domain || "youragency.gov", expires, data.secret || "your-secret");

  const hasToken = isOpenMode
    ? !!(data.secret && data.expiry)
    : !!(data.domain && data.secret && data.expiry);

  // ─── Embed snippets ───────────────────────────────────────────────────────
  const CDN_URL = "https://civicpublish.netlify.app/pdf-ada-widget.js";

  const tokenAttr = hasToken
    ? `  data-token="${token}"`
    : `  data-token=""  <!-- fill in ${isOpenMode ? "Secret" : "Domain + Secret"} above -->`;

  const tokenJs = hasToken
    ? `    token:      "${token}",`
    : `    token:      "",  // fill in ${isOpenMode ? "Secret" : "Domain + Secret"} above`;

  const snippet1 = [
    `<script`,
    `  src="${CDN_URL}"`,
    `  data-brand="${brand}"`,
    `  data-color="${data.color}"`,
    data.logo    ? `  data-logo="${data.logo}"`        : null,
    data.logoAlt ? `  data-logo-alt="${data.logoAlt}"` : null,
    tokenAttr,
    `  data-lang="${data.lang}"`,
    `  data-ocr="${data.ocr}"`,
    `  data-search="${data.search}"`,
    `  data-download="${data.download}"`,
    `  data-copy="${data.copy}"`,
    `  data-share="${data.share}"`,
    `  data-remediate="${data.remediate}"`,
    `  data-max-size-mb="${data.maxSizeMb}"`,
    `  data-selector="${data.selector}"`,
    data.ogImage ? `  data-og-image="${data.ogImage}"` : null,
    `  data-bypass-list="${data.bypassList}"`,
    `  data-allow-types="${data.allowTypes}"`,
    data.dev ? `  data-dev="true"` : null,
    `></script>`,
  ].filter(Boolean).join("\n");

  const snippet2 = [
    `<script>`,
    `  window.WIDGET_CONFIG = {`,
    `    brand:      "${brand}",`,
    `    color:      "${data.color}",`,
    data.logo    ? `    logo:       "${data.logo}",`    : null,
    data.logoAlt ? `    logoAlt:    "${data.logoAlt}",` : null,
    tokenJs,
    `    lang:       "${data.lang}",`,
    `    ocr:        ${data.ocr},`,
    `    search:     ${data.search},`,
    `    download:   ${data.download},`,
    `    copy:       ${data.copy},`,
    `    share:      ${data.share},`,
    `    remediate:  ${data.remediate},`,
    `    maxSizeMb:  ${data.maxSizeMb},`,
    `    selector:   "${data.selector}",`,
    data.ogImage ? `    ogImage:    "${data.ogImage}",` : null,
    `    bypassList: "${data.bypassList}",`,
    `    allowTypes: "${data.allowTypes}",`,
    data.dev ? `    dev:        true,` : null,
    `  };`,
    `<\/script>`,
    `<script src="${CDN_URL}"></script>`,
  ].filter(Boolean).join("\n");

  const snippet3 = [
    `<iframe`,
    `  src="https://accessportal.io/portal/${data.name ? encodeURIComponent(data.name) : "your-agency"}"`,
    `  width="100%"`,
    `  height="600"`,
    `  frameborder="0"`,
    `  title="Accessible Document Portal"`,
    `></iframe>`,
  ].join("\n");

  const TOGGLES = [
    { k:"ocr",       label:"OCR",             desc:"Extract text from scanned PDFs" },
    { k:"search",    label:"Full-text search", desc:"Let visitors search document text" },
    { k:"download",  label:"Download",         desc:"Show download button in viewer" },
    { k:"copy",      label:"Copy text",        desc:"Copy document text to clipboard" },
    { k:"share",     label:"Share link",       desc:"Generate shareable document URL" },
    { k:"remediate", label:"Auto-remediate",   desc:"Auto-fix a11y issues on upload" },
    { k:"dev",       label:"Dev mode",         desc:"Enable verbose console logging" },
  ];

  const CHECKLIST = [
    { done: !!data.name,                                            label: "Agency name set"          },
    { done: !!data.state,                                           label: "State selected"           },
    { done: isOpenMode ? true : !!data.domain,                      label: isOpenMode ? "Open token mode selected (any domain)" : "Domain configured" },
    { done: !!data.secret,                                          label: "Token secret set"         },
    { done: hasToken,                                               label: "Token generated"          },
    { done: !isLocked,                                              label: "Subscription active"      },
    { done: !!pdfFile,                                              label: "PDF uploaded"             },
    { done: !!data.logo,                                            label: "Logo URL configured"      },
    { done: !!data.logoAlt,                                         label: "Logo alt text set"        },
    { done: false,                                                  label: "Script added to <head>"   },
    { done: false,                                                  label: "Tested with screen reader"},
  ];
  const doneCount = CHECKLIST.filter(c => c.done).length;

  // ── PATCH: removed `if (!access.granted) return null` guard ──

  return (
    <>
      <SEO title="Dashboard" noindex />
      <style>{DASH_CSS}</style>
      <div role="status" aria-live="polite" aria-atomic="true" className="dash-sr-only">{msg}</div>
      <a href="#dash-main" className="dash-skip">Skip to main content</a>

      <div className="dash-shell">
        {/* ── Top bar ── */}
        <header className="dash-topbar" role="banner">
          <button type="button" className="dash-topbar__logo"
            onClick={() => navigate("/")} aria-label="AccessPortal — go to home">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M2 4h12M2 8h12M2 12h7"/>
            </svg>
            AccessPortal
          </button>
          <div className="dash-topbar__status" aria-label="Account status">
            <span className={`dash-chip ${access.plan === "pro" ? "dash-chip--pro" : "dash-chip--day"}`}>
              {access.plan === "pro" ? "⚡ Pro" : "☀ Day Pass"}
            </span>
            {isLocked && <span className="dash-chip dash-chip--warn" role="alert">⚠ Expired</span>}
            {saved    && <span className="dash-chip dash-chip--ok">✓ Saved</span>}
          </div>
          <div className="dash-topbar__actions">
            <button type="button" className="dash-btn-ghost" onClick={() => navigate("/")} aria-label="Return to site">← Site</button>
            <button type="button" className="dash-btn-danger"
              onClick={() => { revokeAccess(); navigate("/"); }} aria-label="Sign out">Sign out</button>
          </div>
        </header>

        <DashTabBar active={tab} onChange={changeTab} />

        <div className="dash-body">
          {/* ── Sidebar ── */}
          <aside className="dash-sidebar" role="complementary" aria-label="Agency configuration">

            {/* Agency */}
           {/* ── Token section ── */}
<div className="dash-sb-group">
  <span className="dash-sb-group__label">Token</span>

  <DashField label="Token mode" id="df-tmode"
    hint={isOpenMode
      ? "Works on any website — no domain check"
      : "Only works on the domain you specify below"}>
    <select
      id="df-tmode"
      className="dash-inp dash-inp--sel"
      value={data.tokenMode}
      disabled={!!isLocked}
      onChange={e => {
        set("tokenMode", e.target.value);
        announce(e.target.value === "open"
          ? "Open token mode — works on any website"
          : "Domain-locked token mode");
      }}>
      <option value="domain">Domain-locked (one website)</option>
      <option value="open">Open (any website)</option>
    </select>
  </DashField>

  {!isOpenMode && (
    <DashField label="Domain" id="df-domain" hint="e.g. cityofspringfield.gov" required>
      <input
        id="df-domain"
        className="dash-inp"
        type="text"
        placeholder="youragency.gov"
        value={data.domain}
        disabled={!!isLocked}
        aria-required="true"
        onChange={e => set("domain", e.target.value.toLowerCase().replace(/^www\./, "").trim())}
      />
    </DashField>
  )}

  <DashField label="Secret" id="df-secret" hint="Your private key — keep this safe" required>
    <div style={{ display: "flex", gap: 6 }}>
      <input
        id="df-secret"
        className="dash-inp"
        type="text"
        placeholder="Auto-generate or enter your own"
        value={data.secret}
        disabled={!!isLocked}
        aria-required="true"
        onChange={e => set("secret", e.target.value.trim())}
        style={{ flex: 1 }}
      />
      <button
        type="button"
        className="dash-btn-ghost"
        disabled={!!isLocked}
        onClick={() => {
          const newSecret = generateRandomSecret();
          set("secret", newSecret);
          announce("Random secret generated");
        }}
        aria-label="Generate random secret"
        style={{ 
          padding: "0 10px", 
          flexShrink: 0,
          fontSize: ".7rem",
          whiteSpace: "nowrap"
        }}>
        🎲 Generate
      </button>
    </div>
  </DashField>

  {hasToken ? (
    <div style={{ marginTop: 6 }}>
      <span style={{
        fontSize: ".6rem", fontWeight: 700, letterSpacing: ".08em",
        textTransform: "uppercase", color: "var(--d-label)",
        display: "flex", alignItems: "center", gap: 6, marginBottom: 4,
      }}>
        Generated Token
        <span style={{
          fontWeight: 600, letterSpacing: 0, textTransform: "none",
          fontSize: ".62rem", padding: "1px 7px", borderRadius: 100,
          background: isOpenMode ? "#d1fae5" : "#dceafc",
          color:      isOpenMode ? "#145228" : "#103578",
          border:     isOpenMode ? "1px solid #6ee7b7" : "1px solid #b3d0f5",
        }}>
          {isOpenMode ? "any site" : data.domain}
        </span>
      </span>
      <div style={{
        fontSize: ".58rem", color: "var(--d-label)", 
        marginBottom: 4, lineHeight: 1.5,
        fontFamily: "var(--d-font)"
      }}>
        Format: <code style={{ 
          fontSize: ".58rem", 
          background: "var(--d-bg)", 
          padding: "1px 4px", 
          borderRadius: 3,
          fontFamily: "var(--d-mono)"
        }}>
          {isOpenMode 
            ? "tok_open_{base64(expires)}_{secret}"
            : "tok_{base64(domain)}_{base64(expires)}_{secret}"
          }
        </code>
      </div>
      <code style={{
        display: "block", fontSize: ".58rem",
        fontFamily: "var(--d-mono)", color: "#28c840",
        background: "#0d1b2a", padding: "6px 8px",
        borderRadius: 5, wordBreak: "break-all", lineHeight: 1.6,
      }}>
        {token}
      </code>
      <DashCopyBtn text={token} label="Copy token" />
    </div>
  ) : (
    <div style={{ 
      background: "#fee2e2", 
      border: "1px solid #fca5a5",
      borderRadius: 6,
      padding: "8px 10px",
      marginTop: 4
    }}>
      <p style={{ fontSize: ".68rem", color: "#a71c1c", margin: 0, lineHeight: 1.5 }}>
        {isOpenMode
          ? "⚠ Add a Secret above to generate your open token."
          : "⚠ Add Domain + Secret above to generate your token."}
      </p>
    </div>
  )}
</div>

            <div className="dash-sb-div" role="separator" aria-hidden="true" />

            {/* Appearance */}
            <div className="dash-sb-group">
              <span className="dash-sb-group__label">Appearance</span>
              <DashField label="Brand color" id="df-color">
                <div className="dash-color-row">
                  <input id="df-color" type="color" className="dash-color-swatch"
                    value={data.color} disabled={!!isLocked}
                    onChange={e => set("color", e.target.value)} />
                  <output htmlFor="df-color" className="dash-color-val">{data.color}</output>
                </div>
              </DashField>
              <DashField label="Logo URL" id="df-logo">
                <input id="df-logo" className="dash-inp" type="url" placeholder="https://…/logo.png"
                  value={data.logo} disabled={!!isLocked} onChange={e => set("logo", e.target.value)} />
              </DashField>
              <DashField label="Logo alt text" id="df-logoalt">
                <input id="df-logoalt" className="dash-inp" type="text" placeholder="City seal"
                  value={data.logoAlt} disabled={!!isLocked} onChange={e => set("logoAlt", e.target.value)} />
              </DashField>
            </div>

            {data.created && (
              <>
                <div className="dash-sb-div" role="separator" aria-hidden="true" />
                <dl className="dash-sb-meta">
                  <div className="dash-sb-meta__row"><dt>Created</dt><dd>{data.created}</dd></div>
                  <div className="dash-sb-meta__row">
                    <dt>Expires</dt>
                    <dd style={{ color: isLocked ? "#a71c1c" : "inherit" }}>{data.expiry}</dd>
                  </div>
                </dl>
              </>
            )}

            <div className="dash-sb-spacer" />
            <button type="button" className="dash-btn-reset" onClick={reset}>↺ Reset workspace</button>
          </aside>

          {/* ── Main panels ── */}
          <main id="dash-main" className="dash-main" ref={mainRef} tabIndex={-1} aria-label="Dashboard content">

            {/* Configuration */}
            <div id="dash-panel-config" role="tabpanel" aria-labelledby="dash-tab-config" hidden={tab !== "config"}>
              <div className="dash-panel">
                <div className="dash-panel__hdr">
                  <h1>Configuration</h1>
                  <p>Toggle widget features on or off. Changes auto-save to the URL.</p>
                </div>
                <DashCard title="Feature Toggles" id="features">
                  <div className="dash-toggle-grid" role="group" aria-label="Feature toggles">
                    {TOGGLES.map(({ k, label, desc }) => (
                      <div key={k} className="dash-tog-row">
                        <div className="dash-tog-row__text">
                          <span className="dash-tog-row__name">{label}</span>
                          <span className="dash-tog-row__desc">{desc}</span>
                        </div>
                        <DashToggle id={`dash-tog-${k}`} value={!!data[k]} disabled={!!isLocked}
                          ariaLabel={`${label}: ${data[k] ? "on" : "off"}. ${desc}`}
                          onChange={() => { const n = !data[k]; set(k, n); announce(`${label} ${n ? "on" : "off"}`); }} />
                      </div>
                    ))}
                  </div>
                </DashCard>
              </div>
            </div>

            {/* Upload PDF */}
            <div id="dash-panel-upload" role="tabpanel" aria-labelledby="dash-tab-upload" hidden={tab !== "upload"}>
              <div className="dash-panel">
                <div className="dash-panel__hdr">
                  <h1>Upload PDF</h1>
                  <p>Convert any PDF to WCAG 2.1 AA–compliant HTML instantly.</p>
                </div>
                <DashCard title="PDF Conversion" id="pdf-conv">
                  <div
                    className={`dash-dropzone${pdfDrag ? " dash-dropzone--drag" : ""}`}
                    role="button" tabIndex={0}
                    aria-label={pdfFile
                      ? `Selected: ${pdfFile.name}. Press Enter or Space to change.`
                      : `Drop zone. Press Enter or Space to browse. Max ${data.maxSizeMb} MB.`}
                    onKeyDown={handleDzKey}
                    onDragOver={e => { e.preventDefault(); setPdfDrag(true); }}
                    onDragLeave={() => setPdfDrag(false)}
                    onDrop={e => {
                      e.preventDefault(); setPdfDrag(false);
                      const f = e.dataTransfer.files[0];
                      if (f?.type === "application/pdf") { setPdfFile(f); announce(`Selected: ${f.name}`); }
                      else announce("Only PDF files accepted");
                    }}
                    onClick={() => document.getElementById("dash-pdf-in").click()}>
                    <input id="dash-pdf-in" type="file" accept="application/pdf" className="dash-sr-only"
                      onChange={e => { const f = e.target.files[0]; if (f) { setPdfFile(f); announce(`Selected: ${f.name}`); } }} />
                    {pdfFile ? (
                      <>
                        <span className="dash-dz__icon" aria-hidden="true">📄</span>
                        <span className="dash-dz__name">{pdfFile.name}</span>
                        <span className="dash-dz__sub">Ready — click Convert below</span>
                      </>
                    ) : (
                      <>
                        <span className="dash-dz__icon" aria-hidden="true">☁</span>
                        <span className="dash-dz__text">Drop a PDF here or <span className="dash-dz__link">browse files</span></span>
                        <span className="dash-dz__sub">Max {data.maxSizeMb} MB · Auto-converts to WCAG 2.1 AA HTML</span>
                      </>
                    )}
                  </div>
                  {pdfFile && (
                    <button type="button" className="dash-btn-convert"
                      aria-label={`Convert ${pdfFile.name} to accessible HTML`}
                      onClick={() => alert("Wire to your PDF conversion API.")}>
                      Convert to Accessible HTML →
                    </button>
                  )}
                </DashCard>
                <DashCard title="Conversion Notes" id="conv-notes">
                  <ul className="dash-tip-list">
                    <li><span className="dash-tip-dot" aria-hidden="true" />Tagged PDFs convert faster and with higher fidelity.</li>
                    <li><span className="dash-tip-dot" aria-hidden="true" />Scanned PDFs require OCR — enable it in the Configuration tab.</li>
                    <li><span className="dash-tip-dot" aria-hidden="true" />Maximum file size is controlled by <strong>Max File Size</strong> in Advanced.</li>
                    <li><span className="dash-tip-dot" aria-hidden="true" />Output HTML meets WCAG 2.1 AA automatically — headings, alt text, and reading order are preserved.</li>
                  </ul>
                </DashCard>
              </div>
            </div>

            {/* Advanced */}
            <div id="dash-panel-advanced" role="tabpanel" aria-labelledby="dash-tab-advanced" hidden={tab !== "advanced"}>
              <div className="dash-panel">
                <div className="dash-panel__hdr">
                  <h1>Advanced Settings</h1>
                  <p>Fine-grained configuration options. Changes auto-save to the URL.</p>
                </div>
                <DashCard title="Widget Mounting" id="adv-mount">
                  <div className="dash-adv-grid">
                    <DashField label="Language" id="df-lang" hint="BCP 47 language tag for the widget UI">
                      <select id="df-lang" className="dash-inp dash-inp--sel" value={data.lang}
                        disabled={!!isLocked} onChange={e => set("lang", e.target.value)}>
                        {["en-US","es-ES","fr-FR","de-DE","zh-CN","ar","pt-BR","ja","ko"].map(l => <option key={l}>{l}</option>)}
                      </select>
                    </DashField>
                    <DashField label="CSS Selector" id="df-sel" hint="DOM element where the widget mounts">
                      <input id="df-sel" className="dash-inp" type="text" placeholder="#main-content"
                        value={data.selector} disabled={!!isLocked} onChange={e => set("selector", e.target.value)} />
                    </DashField>
                    <DashField label="Max file size (MB)" id="df-mb" hint="1 – 100 MB accepted">
                      <input id="df-mb" className="dash-inp" type="number" min={1} max={100}
                        value={data.maxSizeMb} disabled={!!isLocked} onChange={e => set("maxSizeMb", Number(e.target.value))} />
                    </DashField>
                    <DashField label="OG Image URL" id="df-og" hint="Social preview image for shared documents">
                      <input id="df-og" className="dash-inp" type="url" placeholder="https://…/og.jpg"
                        value={data.ogImage} disabled={!!isLocked} onChange={e => set("ogImage", e.target.value)} />
                    </DashField>
                  </div>
                </DashCard>
                <DashCard title="Document Filtering" id="adv-filter">
                  <div className="dash-adv-grid">
                    <DashField label="Bypass list" id="df-bypass" hint="Comma-separated keywords — matching docs skip conversion">
                      <input id="df-bypass" className="dash-inp" type="text" placeholder="confidential,draft,internal"
                        value={data.bypassList} disabled={!!isLocked} onChange={e => set("bypassList", e.target.value)} />
                    </DashField>
                    <DashField label="Allow types" id="df-types" hint="Comma-separated doc types eligible for conversion">
                      <input id="df-types" className="dash-inp" type="text" placeholder="minutes,agenda,report,budget"
                        value={data.allowTypes} disabled={!!isLocked} onChange={e => set("allowTypes", e.target.value)} />
                    </DashField>
                  </div>
                </DashCard>
              </div>
            </div>

            {/* Live Preview */}
            <div id="dash-panel-preview" role="tabpanel" aria-labelledby="dash-tab-preview" hidden={tab !== "preview"}>
              <div className="dash-panel">
                <div className="dash-panel__hdr">
                  <h1>Live Preview</h1>
                  <p>What visitors see when they click a PDF link on your site.</p>
                </div>
                <figure className="dash-browser" aria-label="Widget browser preview">
                  <figcaption className="dash-sr-only">Browser preview for {data.name || "your agency"}.</figcaption>
                  <div className="dash-browser__bar" aria-hidden="true">
                    <div className="dash-browser__dots">
                      {["#ff5f57","#febc2e","#28c840"].map(c =>
                        <span key={c} style={{ width:11, height:11, borderRadius:"50%", background:c, display:"inline-block" }}/>
                      )}
                    </div>
                    <div className="dash-browser__url">
                      {data.domain && !isOpenMode
                        ? `${data.domain}/documents`
                        : data.name
                          ? `${data.name.toLowerCase().replace(/ /g,"-")}.gov/documents`
                          : "your-agency.gov/documents"
                      }
                    </div>
                  </div>
                  <div className="dash-browser__body" aria-hidden="true">
                    <div className="dash-modal">
                      <div className="dash-modal__hdr" style={{ borderLeft:`4px solid ${data.color}` }}>
                        {data.logo && <img src={data.logo} alt="" className="dash-modal__logo" />}
                        <div>
                          <div className="dash-modal__title">Sample Document 2025.pdf</div>
                          <div className="dash-modal__sub">{data.name || "Agency Name"}{data.state ? ` · ${data.state}` : ""}</div>
                        </div>
                        <div className="dash-modal__close">✕</div>
                      </div>
                      <div className="dash-modal__body">
                        <div className="dash-wcag-chip"><span className="dash-wcag-dot"/>WCAG 2.1 AA Compliant</div>
                        <div className="dash-mock-lines">
                          {[null,"92%","85%","78%","88%","60%"].map((w, i) => (
                            <div key={i} className={`dash-mock-line${i === 0 ? " dash-mock-line--h" : ""}`} style={w ? { width:w } : {}} />
                          ))}
                        </div>
                      </div>
                      <div className="dash-modal__foot">
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {["Screen reader ✓","Keyboard nav ✓","Alt text ✓"].map(t => (
                            <span key={t} className="dash-preview-tag">{t}</span>
                          ))}
                        </div>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {data.download && <span className="dash-preview-act" style={{ borderColor:data.color, color:data.color }}>Download</span>}
                          {data.copy     && <span className="dash-preview-act" style={{ borderColor:data.color, color:data.color }}>Copy text</span>}
                          {data.share    && <span className="dash-preview-act" style={{ borderColor:data.color, color:data.color }}>Share ↗</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </figure>
                <section className="dash-cfg-summary" aria-labelledby="dash-cfg-h">
                  <h2 id="dash-cfg-h" className="dash-cfg-summary__title">Active configuration</h2>
                  <dl className="dash-cfg-summary__grid">
                    {[
                      ["Brand color", data.color, true],
                      ["Token mode",  isOpenMode ? "Open (any site)" : "Domain-locked", false],
                      ["Domain",      isOpenMode ? "Any website" : (data.domain || "—"), false],
                      ["Token",
                        hasToken
                          ? `✓ ${isOpenMode ? "Open token" : "Domain token"}`
                          : isOpenMode ? "⚠ Add secret" : "⚠ Add domain+secret",
                        false],
                      ["Language",    data.lang,  false],
                      ["Max size",    `${data.maxSizeMb} MB`, false],
                      ["OCR",         data.ocr  ? "On" : "Off", false],
                      ["Search",      data.search ? "On" : "Off", false],
                      ["Download",    data.download ? "On" : "Off", false],
                      ["Share",       data.share ? "On" : "Off", false],
                      ["Dev mode",    data.dev ? "On" : "Off", false],
                    ].map(([k, v, sw]) => (
                      <div key={k} className="dash-cfg-summary__item">
                        <dt>{k}</dt>
                        <dd>
                          {sw && <span className="dash-color-swatch-sm" style={{ background:data.color }} role="img" aria-label={`Color ${data.color}`} />}
                          {v}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </div>
            </div>

            {/* Embed Code */}
            <div id="dash-panel-embed" role="tabpanel" aria-labelledby="dash-tab-embed" hidden={tab !== "embed"}>
              <div className="dash-panel">
                <div className="dash-panel__hdr">
                  <h1>Embed Code</h1>
                  <p>
                    Both snippets are generated from your current configuration.
                    {!hasToken && (
                      <strong style={{ color: "#a71c1c", display: "block", marginTop: 4 }}>
                        ⚠ {isOpenMode
                          ? "Add a Secret in the sidebar to generate your open token."
                          : "Add Domain + Secret in the sidebar to generate your token."}
                      </strong>
                    )}
                  </p>
                </div>
                <DashCard title="Method 1 — data-* attributes" id="m1">
                  <p className="dash-note">Paste this <code>&lt;script&gt;</code> tag into your site's <code>&lt;head&gt;</code>. No JavaScript config object needed.</p>
                  <DashCodeBlock id="dcb1" filename="head.html" code={snippet1} />
                  <DashCopyBtn text={snippet1} label="Copy data-attribute snippet" />
                </DashCard>
                <DashCard title="Method 2 — window.WIDGET_CONFIG" id="m2">
                  <p className="dash-note">Define the config object <strong>before</strong> the widget script. The widget reads <code>window.WIDGET_CONFIG</code> on load — order matters.</p>
                  <DashCodeBlock id="dcb2" filename="head.html" code={snippet2} />
                  <DashCopyBtn text={snippet2} label="Copy window.WIDGET_CONFIG snippet" />
                </DashCard>
                <DashCard title="Portal iFrame" id="m3">
                  <p className="dash-note">Embed the fully accessible, searchable document portal anywhere on your site.</p>
                  <DashCodeBlock id="dcb3" filename="portal-page.html" code={snippet3} />
                  <DashCopyBtn text={snippet3} label="Copy iframe code" />
                </DashCard>
                <section className="dash-checklist" aria-labelledby="dash-cl-h">
                  <h2 id="dash-cl-h" className="dash-checklist__title">
                    Go-live checklist
                    <span className="dash-cl-progress" aria-label={`${doneCount} of ${CHECKLIST.length} complete`}>{doneCount}/{CHECKLIST.length}</span>
                  </h2>
                  <ul className="dash-cl-list">
                    {CHECKLIST.map((item, i) => (
                      <li key={i} className={`dash-cl-item${item.done ? " dash-cl-item--done" : ""}`}>
                        <span className="dash-cl-icon" aria-hidden="true">{item.done ? "✓" : "○"}</span>
                        {item.label}
                        <span className="dash-sr-only"> — {item.done ? "complete" : "incomplete"}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>

          </main>
        </div>
      </div>
    </>
  );
}

// ─── Category badge ───────────────────────────────────────────────────────────
const CAT_COLORS = {
  Accessibility:  { bg:"#e8f0fe", text:"#1a367a" },
  Research:       { bg:"#fef3c7", text:"#7c4a00" },
  Compliance:     { bg:"#d1fae5", text:"#065f46" },
  Implementation: { bg:"#fce7f3", text:"#9d174d" },
  "Higher Ed":    { bg:"#ede9fe", text:"#4c1d95" },
};

function CategoryBadge({ category }) {
  const { bg, text } = CAT_COLORS[category] || { bg:"#f3f4f6", text:"#374151" };
  return <span className="blog-cat" style={{ background:bg, color:text }}>{category}</span>;
}

// ─── Blog grid card ───────────────────────────────────────────────────────────
function BlogCard({ post, headingLevel = "h3" }) {
  const navigate = useNavigate();
  const Heading  = headingLevel;
  return (
    <article className="blog-card" onClick={() => navigate(`/blog/${post.slug}`)}>
      <div className="blog-card-top">
        <CategoryBadge category={post.category} />
      </div>
      <div className="blog-card-body">
        <Heading className="blog-card-title">{post.title}</Heading>
        <p>{post.excerpt}</p>
        <div className="blog-meta">
          <time dateTime={post.dateIso}>{post.date}</time>
          <span>{post.readTime}</span>
        </div>
      </div>
      <div className="blog-card-footer">
        <span className="blog-read-more">Read article →</span>
      </div>
    </article>
  );
}

// ─── Home page ────────────────────────────────────────────────────────────────
function HomePage() {
  const navigate = useNavigate();
  const f1 = useFade(), f2 = useFade(), f3 = useFade(), f4 = useFade(), f5 = useFade();

  const orgLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "AccessPortal",
    "applicationCategory": "WebApplication",
    "description": "PDF accessibility widget for government and educational websites. Converts PDFs to WCAG 2.1 AA compliant HTML automatically.",
    "url": "https://accessportal.io",
    "offers": [
      { "@type":"Offer", "name":"Starter",      "price":"99",  "priceCurrency":"USD" },
      { "@type":"Offer", "name":"Professional", "price":"249", "priceCurrency":"USD" },
    ],
  });

  return (
    <>
      <SEO
        canonical="/"
        description="One script tag makes every PDF on your government or university site instantly accessible and WCAG 2.1 AA compliant. ADA Title II compliance without manual remediation."
      />
      <script type="application/ld+json" suppressHydrationWarning>{orgLd}</script>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-dots" />
        <div className="hero-mesh" />
        <div className="hero-inner">
          <div className="hero-content">
            <div className="hero-badge"><span className="badge-dot" />DOJ ADA Title II — April 2026 deadline passed</div>
            <h1>Every PDF on your site,<br /><em>accessible instantly.</em></h1>
            <p className="hero-sub">One script tag makes every PDF click open as accessible, WCAG 2.1 AA compliant HTML. Or let us convert your entire document library into a searchable, Google-indexed portal.</p>
            <div className="hero-ctas">
              <button className="btn-primary" onClick={() => window.location.href = STRIPE.links.pro}>Start free trial — no card needed</button>
              <button className="btn-ghost"   onClick={() => document.getElementById("how")?.scrollIntoView({ behavior:"smooth" })}>See how it works</button>
            </div>
            <div className="social-proof">
              <div className="avatars">
                {["JR","KM","AL","TP","SR"].map((ini, n) => <div key={ini} className={`av av${n+1}`}>{ini}</div>)}
              </div>
              <div className="proof-text"><strong>Trusted by school districts,</strong><br />county agencies &amp; gov IT teams</div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-svg-wrap">
              <svg viewBox="0 0 520 380" xmlns="http://www.w3.org/2000/svg" style={{ width:"100%", height:"auto" }}>
                <defs>
                  <filter id="card-shadow" x="-10%" y="-10%" width="120%" height="130%">
                    <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="rgba(0,0,0,0.3)"/>
                  </filter>
                  <linearGradient id="html-grad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#f7f9fc"/><stop offset="100%" stopColor="#eef2f8"/>
                  </linearGradient>
                  <linearGradient id="modal-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff"/><stop offset="100%" stopColor="#f4f7fc"/>
                  </linearGradient>
                </defs>
                <rect x="18" y="28" width="320" height="220" rx="12" fill="url(#html-grad)" filter="url(#card-shadow)" opacity="0.7"/>
                <rect x="18" y="28" width="320" height="32" rx="12" fill="#dde5f0"/>
                <rect x="18" y="48" width="320" height="12" fill="#dde5f0"/>
                <circle cx="34" cy="44" r="5" fill="#ff5f57"/>
                <circle cx="50" cy="44" r="5" fill="#febc2e"/>
                <circle cx="66" cy="44" r="5" fill="#28c840"/>
                <rect x="80" y="36" width="200" height="16" rx="8" fill="rgba(255,255,255,0.7)"/>
                <text x="180" y="48" fontSize="8" fill="#8899bb" textAnchor="middle" fontFamily="monospace">lincoln-unified.k12.us/docs</text>
                <rect x="34" y="74" width="120" height="9" rx="4" fill="#c5d3e8"/>
                <rect x="34" y="90"  width="280" height="6" rx="3" fill="#dce6f2"/>
                <rect x="34" y="102" width="230" height="6" rx="3" fill="#dce6f2"/>
                <rect x="34" y="114" width="260" height="6" rx="3" fill="#dce6f2"/>
                <rect x="34" y="132" width="180" height="28" rx="7" fill="rgba(24,80,160,0.08)" stroke="#b5c9e8" strokeWidth="1"/>
                <rect x="44" y="139" width="16" height="14" rx="3" fill="#1850a0"/>
                <text x="49" y="149" fontSize="6" fill="#fff" fontWeight="700" fontFamily="sans-serif">PDF</text>
                <text x="68" y="149" fontSize="8" fill="#1850a0" fontFamily="sans-serif" fontWeight="500">Student Handbook 2025.pdf</text>
                <text x="196" y="149" fontSize="7" fill="#6a8fbf" fontFamily="sans-serif">↗</text>
                <g opacity="0.9">
                  <line x1="348" y1="155" x2="378" y2="155" stroke="#f5a623" strokeWidth="2.5" strokeLinecap="round"/>
                  <polyline points="372,148 380,155 372,162" fill="none" stroke="#f5a623" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <text x="362" y="148" fontSize="7" fill="#f5a623" textAnchor="middle" fontFamily="sans-serif" fontWeight="600">click</text>
                </g>
                <rect x="188" y="105" width="310" height="255" rx="14" fill="url(#modal-grad)" filter="url(#card-shadow)"/>
                <rect x="188" y="105" width="310" height="38" rx="14" fill="#1850a0"/>
                <rect x="188" y="120" width="310" height="23" fill="#1850a0"/>
                <text x="204" y="128" fontSize="9" fill="rgba(255,255,255,0.7)" fontFamily="sans-serif">Student Handbook 2025</text>
                <circle cx="480" cy="124" r="8" fill="rgba(255,255,255,0.15)"/>
                <text x="480" y="128" fontSize="9" fill="#fff" textAnchor="middle" fontFamily="sans-serif">✕</text>
                <circle cx="213" cy="162" r="4" fill="#28a745"/>
                <text x="220" y="166" fontSize="7.5" fill="#1850a0" fontWeight="600" fontFamily="sans-serif">WCAG 2.1 AA Compliant</text>
                <rect x="204" y="180" width="200" height="9" rx="4" fill="#0f1f2e" opacity="0.75"/>
                <rect x="204" y="196" width="270" height="6" rx="3" fill="#c5d0dc"/>
                <rect x="204" y="208" width="250" height="6" rx="3" fill="#c5d0dc"/>
                <rect x="204" y="220" width="265" height="6" rx="3" fill="#c5d0dc"/>
                <rect x="428" y="298" width="52" height="18" rx="9" fill="#dcfce7" stroke="#a7f3d0" strokeWidth=".75"/>
                <text x="454" y="311" fontSize="7" fill="#166534" textAnchor="middle" fontFamily="sans-serif" fontWeight="600">✓ Compliant</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <div className="trust-bar">
        <div className="trust-inner">
          {[
            { label:"WCAG 2.1 Level AA",       path:"M9 2l2 4 5 .7-3.5 3.5.8 5L9 13l-4.3 2.2.8-5L2 6.7l5-.7z" },
            { label:"ADA Title II Compliant",   path:"M2 3h14v12H2z M6 7h6M6 11h4" },
            { label:"Live in under 20 minutes", path:"M9 2a7 7 0 100 14A7 7 0 009 2zM9 5v4l3 2" },
            { label:"Works with any CMS",       path:"M3 9l4 4 8-8" },
          ].map((item, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:"2.5rem", flexWrap:"wrap" }}>
              {i > 0 && <div className="trust-divider" />}
              <div className="trust-item">
                <svg className="trust-icon" viewBox="0 0 18 18" fill="none" stroke="#1850a0" strokeWidth="2" strokeLinecap="round">
                  <path d={item.path}/>
                </svg>
                {item.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <section className="section" id="how">
        <div className="section-inner">
          <div className="fade" ref={f1}>
            <p className="section-label">How it works</p>
            <h2 className="section-title">Compliance in four steps</h2>
            <p className="section-sub">No dev team required. No IT overhaul. Paste one line of code and every PDF on your site is instantly accessible.</p>
            <div className="steps">
              {[
                { n:"01", t:"Add one script tag",           p:"Paste a single line into your site's <head>. Works with WordPress, Drupal, Granicus, and any custom CMS." },
                { n:"02", t:"Widget finds all PDFs",        p:"AccessPortal scans your pages automatically and intercepts every PDF link — no manual tagging needed." },
                { n:"03", t:"Visitors see accessible HTML", p:"Every PDF click opens a WCAG 2.1 AA compliant HTML modal. Screen reader ready, keyboard navigable." },
                { n:"04", t:"New PDFs auto-convert",        p:"Upload a new PDF to your site? It converts automatically. You never have to think about it again." },
              ].map(s => (
                <div className="step" key={s.n}>
                  <div className="step-num">STEP {s.n}</div>
                  <div className="step-ico">
                    <svg viewBox="0 0 18 18" style={{ width:19, height:19, stroke:"var(--blue)", fill:"none", strokeWidth:2, strokeLinecap:"round", strokeLinejoin:"round" }}>
                      <path d="M3 3h12M3 7h12M3 11h8"/>
                    </svg>
                  </div>
                  <h3>{s.t}</h3><p>{s.p}</p>
                </div>
              ))}
            </div>
            <div className="code-card">
              <div className="code-top">
                <div className="code-dot cd-r"/><div className="code-dot cd-y"/><div className="code-dot cd-g"/>
                <span className="code-file">head.html — add this once, done forever</span>
              </div>
              <div className="code-body">
                <span className="cm">{"<!-- AccessPortal — paste into your site <head> -->"}</span><br/>
                <span className="ck">&lt;script</span><br/>
                &nbsp;&nbsp;<span className="cv">src</span>=<span className="cs">"https://civicpublish.netlify.app/pdf-ada-widget.js"</span><br/>
                &nbsp;&nbsp;<span className="cv">data-token</span>=<span className="cs">"your-generated-token"</span><br/>
                <span className="ck">&gt;&lt;/script&gt;</span>
              </div>
            </div>
            <div className="stat-row">
              {[["500+","Government sites protected"],["< 20m","Average time to go live"],["100%","WCAG 2.1 AA pass rate"],["0","IT tickets required"]].map(([n,l]) => (
                <div key={l}><div className="stat-num">{n}</div><div className="stat-label">{l}</div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portal */}
      <section className="section" id="portal" style={{ background:"var(--off)", borderTop:"1px solid var(--border)", borderBottom:"1px solid var(--border)" }}>
        <div className="section-inner">
          <div className="fade" ref={f2}>
            <div className="urgency-card">
              <div className="urg-icon">
                <svg viewBox="0 0 22 22" style={{ width:22, height:22, stroke:"var(--red)", fill:"none", strokeWidth:2, strokeLinecap:"round" }}>
                  <path d="M11 3l9 16H2L11 3z"/><path d="M11 9v5M11 16v.5"/>
                </svg>
              </div>
              <div style={{ flex:1 }}>
                <div className="urg-title">The DOJ deadline has passed — April 24, 2026</div>
                <div className="urg-sub">Public entities with populations over 50,000 must comply <strong>right now.</strong> Every non-accessible PDF on your site is a <strong>legal liability.</strong> Smaller entities have until April 2027.</div>
              </div>
              <button className="btn-primary" style={{ whiteSpace:"nowrap", flexShrink:0 }} onClick={() => window.location.href = STRIPE.links.pro}>Fix it today →</button>
            </div>
            <p className="section-label" style={{ marginTop:"3rem" }}>Document portal</p>
            <h2 className="section-title">Convert your entire PDF library<br/>into a searchable HTML portal</h2>
            <p className="section-sub">Send us your documents. We convert every one into a perfectly structured HTML page and deliver a portal you embed with one line of code.</p>
            <div className="steps-3">
              {[
                { t:"1. Secure Transfer", p:"Give us a sitemap or dump your historical documents into our secure transfer zone." },
                { t:"2. AI Processing",   p:"Our engine accurately extracts text, headers, and reading order to create WCAG AA compliant code." },
                { t:"3. Embed Portal",    p:"Drop the custom iframe into your site to instantly deploy a fully searchable document repository." },
              ].map(s => (
                <div className="step" key={s.t}>
                  <div className="step-ico">
                    <svg viewBox="0 0 18 18" style={{ width:19, height:19, stroke:"var(--blue)", fill:"none", strokeWidth:2, strokeLinecap:"round", strokeLinejoin:"round" }}>
                      <rect x="3" y="3" width="12" height="12" rx="2"/><path d="M9 9h.01"/>
                    </svg>
                  </div>
                  <h3>{s.t}</h3><p>{s.p}</p>
                </div>
              ))}
            </div>
            <div className="portal-pricing-strip">
              <div className="pps-left">
                <h3>Bulk historical document conversion</h3>
                <p>Special one-time pricing for government and educational document libraries.</p>
              </div>
              <div className="pps-tiers">
                {[["Up to 1,000 PDFs","$499","one-time flat fee"],["Up to 5,000 PDFs","$1,499","one-time flat fee"],["10k+ PDFs","Custom","Talk to sales"]].map(([vol,price,note]) => (
                  <div className="pps-tier" key={vol}>
                    <div className="pps-vol">{vol}</div>
                    <div className="pps-price">{price}</div>
                    <div className="pps-note">{note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section" id="pricing">
        <div className="section-inner">
          <div className="fade" ref={f3} style={{ textAlign:"center", maxWidth:600, margin:"0 auto 3rem" }}>
            <p className="section-label" style={{ justifyContent:"center" }}>Pricing</p>
            <h2 className="section-title">Simple plans, zero surprises</h2>
            <p className="section-sub" style={{ margin:"0 auto" }}>Choose the plan that fits your website's traffic. Every plan includes full, automated ADA Title II compliance features.</p>
          </div>
          <div className="pricing-grid">
            {[
              {
                tag:"Starter", price:"$99", mo:"per month, billed annually",
                desc:"Perfect for small municipal websites and individual schools.",
                features:["Up to 10k pageviews/mo","Unlimited PDFs processed","WCAG 2.1 AA HTML Modal","Standard support"],
                cta:() => window.location.href = STRIPE.links.daypass, ctaLabel:"Start 14-day free trial", ghost:true,
              },
              {
                tag:"Professional", price:"$249", mo:"per month, billed annually",
                desc:"For cities, counties, and complete school districts.",
                features:["Up to 100k pageviews/mo","Unlimited PDFs processed","Compliance dashboard reporting","Priority scanning queue","24/7 Priority support"],
                cta:() => window.location.href = STRIPE.links.pro, ctaLabel:"Start 14-day free trial", featured:true,
              },
              {
                tag:"Enterprise", price:"Custom", mo:"tailored to your needs",
                desc:"For state agencies and massive institutional document libraries.",
                features:["Unlimited pageviews","Custom data retention","Dedicated account manager","SLA guarantees","Custom MSA and invoicing"],
                cta:() => {}, ctaLabel:"Contact Sales", ghost:true,
              },
            ].map(plan => (
              <div key={plan.tag} className={`p-card${plan.featured ? " featured" : ""}`}>
                {plan.featured && <div className="pop-badge">Most Popular</div>}
                <div className="plan-tag">{plan.tag}</div>
                <div className="plan-price">
                  {plan.price === "Custom" ? plan.price : <><sup>$</sup>{plan.price.slice(1)}</>}
                </div>
                <div className="plan-mo">{plan.mo}</div>
                <div className="plan-desc">{plan.desc}</div>
                <ul className="feat-list">
                  {plan.features.map(f => <li key={f}><div className="fc"><CheckIcon/></div> {f}</li>)}
                </ul>
                <button className={plan.ghost ? "btn-blue-ghost" : "btn-primary"} style={{ width:"100%" }} onClick={plan.cta}>
                  {plan.ctaLabel}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" style={{ background:"var(--off2)" }}>
        <div className="section-inner">
          <div className="fade" ref={f4} style={{ textAlign:"center", maxWidth:600, margin:"0 auto 3rem" }}>
            <p className="section-label" style={{ justifyContent:"center" }}>Testimonials</p>
            <h2 className="section-title">Trusted by public entities everywhere</h2>
          </div>
          <div className="testi-grid">
            {[
              { i:"MD", name:"Marcus Davis",  role:"IT Director, Lincoln County", col:"#2563c4", quote:"The April deadline had our department panicking. We had over 4,000 PDFs spanning decades of board meetings. AccessPortal fixed our compliance issue in under an hour." },
              { i:"SJ", name:"Sarah Jenkins", role:"Webmaster, Springfield USD",   col:"#059669", quote:"We looked at manual remediation, but quoting $5 per page for our 200-page budget reports was insane. The automated widget completely saved our department's budget." },
            ].map(t => (
              <div className="t-card" key={t.name}>
                <div className="t-stars">★★★★★</div>
                <blockquote className="t-quote">{t.quote}</blockquote>
                <div className="t-author">
                  <div className="t-av" style={{ background:t.col }}>{t.i}</div>
                  <div><div className="t-name">{t.name}</div><div className="t-role">{t.role}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog preview */}
      <section className="section">
        <div className="section-inner">
          <div className="fade" ref={f5}>
            <p className="section-label">From the blog</p>
            <h2 className="section-title">Why HTML beats PDF</h2>
            <p className="section-sub">The technical and legal case for making the switch — written for government IT teams.</p>
            <div className="blog-grid">
              {BLOG_POSTS.slice(0, 3).map(post => <BlogCard key={post.slug} post={post} />)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-mesh" />
        <div className="cta-inner">
          <h2>Ready to eliminate your PDF liability?</h2>
          <p>Join hundreds of government and educational organizations who sleep soundly knowing their documents are 100% ADA compliant.</p>
          <button className="btn-primary" onClick={() => window.location.href = STRIPE.links.pro}>Start your 14-day free trial</button>
          <p style={{ fontSize:".8rem", marginTop:"1rem", color:"rgba(255,255,255,0.4)", marginBottom:0 }}>No credit card required. Cancel anytime.</p>
        </div>
      </section>
    </>
  );
}

// ─── Blog list page ───────────────────────────────────────────────────────────
function BlogListPage() {
  const f1         = useFade();
  const categories = ["All", ...Array.from(new Set(BLOG_POSTS.map(p => p.category)))];
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? BLOG_POSTS : BLOG_POSTS.filter(p => p.category === active);

  return (
    <>
      <SEO
        title="Blog — PDF Accessibility for Government"
        description="Technical guides and compliance resources for government IT teams navigating ADA Title II PDF accessibility requirements."
        canonical="/blog"
        type="website"
      />
      <div style={{ background:"linear-gradient(135deg,var(--navy),var(--navy-mid))", padding:"7rem 5% 3.5rem", marginTop:64 }}>
        <div style={{ maxWidth:1160, margin:"0 auto" }}>
          <p className="section-label" style={{ color:"rgba(126,184,255,0.8)", marginBottom:".5rem" }}>Blog</p>
          <h1 style={{ fontFamily:"var(--font)", fontSize:"clamp(2rem,4vw,3rem)", color:"#fff", marginBottom:"1rem", fontWeight:800 }}>
            HTML vs PDF: The Full Picture
          </h1>
          <p style={{ color:"rgba(255,255,255,.6)", fontSize:".95rem", fontWeight:300, maxWidth:480, lineHeight:1.8 }}>
            Research and technical guides for government IT teams navigating ADA Title II compliance.
          </p>
        </div>
      </div>
      <section className="section">
        <div className="section-inner">
          <div className="blog-filter-bar" role="group" aria-label="Filter by category">
            {categories.map(cat => (
              <button key={cat}
                className={`blog-filter-btn${active === cat ? " blog-filter-btn--on" : ""}`}
                onClick={() => setActive(cat)} aria-pressed={active === cat}>
                {cat}
                {cat !== "All" && (
                  <span className="blog-filter-count">{BLOG_POSTS.filter(p => p.category === cat).length}</span>
                )}
              </button>
            ))}
          </div>
          <div className="fade" ref={f1}>
            <div className="blog-grid blog-grid--full">
              {filtered.map(post => <BlogCard key={post.slug} post={post} headingLevel="h2" />)}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── Blog post page ───────────────────────────────────────────────────────────
function BlogPostPage({ slug }) {
  const navigate = useNavigate();
  const post     = BLOG_POSTS.find(p => p.slug === slug);
  const f1       = useFade();

  if (!post) return <NotFoundPage />;

  const PostContent = post.component;
  const related     = BLOG_POSTS.filter(p => p.slug !== slug && p.category === post.category).slice(0, 2);

  return (
    <>
      <SEO
        title={post.title}
        description={post.description || post.excerpt}
        canonical={`/blog/${post.slug}`}
        type="article"
      />
      <ArticleJsonLd post={post} />
      <style>{BLOG_POST_CSS}</style>

      <div className="blog-hero-bar">
        <div className="blog-hero-bar-inner">
          <button className="back-link" onClick={() => navigate("/blog")}>← Back to blog</button>
          <h1 className="post-title">{post.title}</h1>
          <div className="post-meta">
            <CategoryBadge category={post.category} />
            <time dateTime={post.dateIso}>{post.date}</time>
            <span>{post.readTime}</span>
          </div>
        </div>
      </div>

      <div className="blog-post">
        <div className="blog-post-inner fade" ref={f1}>
          <PostContent />
        </div>
        {related.length > 0 && (
          <aside className="blog-related">
            <h2 className="blog-related__title">Related articles</h2>
            <div className="blog-related__grid">
              {related.map(rp => (
                <article key={rp.slug} className="blog-related__card" onClick={() => navigate(`/blog/${rp.slug}`)}>
                  <CategoryBadge category={rp.category} />
                  <h3>{rp.title}</h3>
                  <span className="blog-read-more">Read →</span>
                </article>
              ))}
            </div>
          </aside>
        )}
        <div style={{ maxWidth:780, margin:"3rem auto 0", paddingTop:"2rem", borderTop:"1px solid var(--border)", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
          <button className="btn-blue-ghost" onClick={() => navigate("/blog")}>← All posts</button>
          <button className="btn-primary" onClick={() => window.location.href = STRIPE.links.pro}>Start free trial →</button>
        </div>
      </div>
    </>
  );
}

// ─── Checkout success ─────────────────────────────────────────────────────────
const PLAN_LABELS = { daypass:"Day Pass", pro:"Pro" };
const PLAN_NOTES  = {
  daypass: "Keep this tab open while you work. Access is active for this session only.",
  pro:     "Your Pro subscription is active. Access persists across browser sessions.",
};
const PRO_FEATURES = [
  "WCAG 2.1 AA compliant HTML modal",
  "Unlimited PDF processing",
  "Auto-detect new PDFs",
  "Compliance dashboard & reporting",
  "Priority scanning queue",
  "24/7 priority support",
];

function CheckoutSuccessPage() {
  const navigate      = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const urlRef  = params.get("ref");
    const urlPlan = params.get("plan") || "daypass";
    if (urlRef && isValidRef(urlRef)) {
      grantAccess(urlRef, urlPlan);
      window.history.replaceState({}, "", "/checkout/success");
      setState({ granted:true, plan:urlPlan });
    } else {
      setState(checkAccess());
    }
  }, []);

  if (!state) return (
    <div className="checkout-page">
      <SEO title="Setting up your account…" noindex />
      <div className="checkout-inner">
        <p style={{ color:"var(--muted)", fontSize:14, textAlign:"center" }}>Verifying your access…</p>
      </div>
    </div>
  );

  const { granted, plan } = state;
  const planLabel = PLAN_LABELS[plan] || "Pro";

  return (
    <div className="checkout-page">
      <SEO title={granted ? "You're all set!" : "No session found"} noindex />
      <div className="checkout-inner">
        {granted ? (
          <>
            <svg width="72" height="72" viewBox="0 0 72 72" fill="none" style={{ margin:"0 auto 1.5rem", display:"block" }}>
              <circle cx="36" cy="36" r="36" fill="rgba(24,80,160,.08)"/>
              <circle cx="36" cy="36" r="30" stroke="#1850a0" strokeWidth="1.5" fill="none"/>
              <path d="M22 36l9 9 18-18" stroke="#1850a0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <p style={{ fontSize:".72rem", fontWeight:700, letterSpacing:".12em", textTransform:"uppercase", color:"var(--blue)", marginBottom:".5rem" }}>
              {planLabel} — Access Active
            </p>
            <h1>You're all set.</h1>
            <div className="plan-note">{PLAN_NOTES[plan] || PLAN_NOTES.pro}</div>
            <div className="pro-features">
              <div className="pro-features-label">What's included</div>
              {PRO_FEATURES.map(f => (
                <div className="pro-feat-item" key={f}>
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" style={{ flexShrink:0 }}>
                    <circle cx="8.5" cy="8.5" r="8.5" fill="rgba(24,80,160,.12)"/>
                    <path d="M5 8.5l2.5 2.5 5-5" stroke="#1850a0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {f}
                </div>
              ))}
            </div>
            <div className="btn-group" style={{ justifyContent:"center" }}>
              <button className="btn-primary"    onClick={() => navigate("/dashboard")}>Open Dashboard ↗</button>
              <button className="btn-blue-ghost" onClick={() => navigate("/blog")}>Read the Blog</button>
            </div>
            <p style={{ fontSize:12, color:"var(--muted)", marginTop:"1.5rem" }}>
              Questions? <a href="mailto:support@accessportal.io" style={{ color:"var(--blue)" }}>support@accessportal.io</a>
            </p>
            <button className="revoke-btn" onClick={() => { revokeAccess(); navigate("/"); }}>
              {plan === "daypass" ? "End session" : "Sign out of this device"}
            </button>
          </>
        ) : (
          <>
            <h2>No active session found.</h2>
            <p>Day Pass access ends when the tab closes. Pro access persists but is tied to the browser you purchased from.</p>
            <p>If you completed a purchase and can't get in, email us and we'll sort it out immediately.</p>
            <div className="btn-group" style={{ justifyContent:"center" }}>
              <a className="btn-primary" href="mailto:support@accessportal.io?subject=Purchase%20Access%20Issue">Contact support →</a>
              <button className="btn-blue-ghost" onClick={() => navigate("/#pricing")}>Back to Pricing</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Checkout cancel ──────────────────────────────────────────────────────────
function CheckoutCancelPage() {
  const navigate = useNavigate();
  return (
    <div className="checkout-page">
      <SEO title="Payment cancelled" noindex />
      <div className="checkout-inner">
        <p style={{ fontSize:".72rem", fontWeight:700, letterSpacing:".12em", textTransform:"uppercase", color:"var(--muted)", marginBottom:".5rem" }}>No Charge Made</p>
        <h1>Payment cancelled.</h1>
        <p>No worries — you haven't been charged. Come back anytime.</p>
        <div className="btn-group" style={{ justifyContent:"center" }}>
          <button className="btn-primary"    onClick={() => window.location.href = STRIPE.links.pro}>Back to Pricing</button>
          <button className="btn-blue-ghost" onClick={() => navigate("/")}>Go Home</button>
        </div>
      </div>
    </div>
  );
}

// ─── 404 ──────────────────────────────────────────────────────────────────────
function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <>
      <SEO title="404 — Page Not Found" noindex />
      <div style={{ padding:"7rem 5%", textAlign:"center", minHeight:"60vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div>
          <div style={{ fontFamily:"var(--font)", fontSize:"5rem", fontWeight:800, color:"var(--blue)", opacity:.15, lineHeight:1 }}>404</div>
          <h1 style={{ marginTop:16, marginBottom:12 }}>Page not found.</h1>
          <p style={{ color:"var(--muted)", marginBottom:32 }}>The page you're looking for doesn't exist or may have moved.</p>
          <div className="btn-group" style={{ justifyContent:"center" }}>
            <button className="btn-primary"    onClick={() => navigate("/")}>Go Home</button>
            <button className="btn-blue-ghost" onClick={() => navigate("/blog")}>Read the Blog</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Routes ───────────────────────────────────────────────────────────────────
const ROUTES = [
  { path:"/",                 component: HomePage             },
  { path:"/dashboard",        component: Dashboard            },
  { path:"/blog",             component: BlogListPage         },
  { path:"/blog/:slug",       component: ({ slug }) => <BlogPostPage slug={slug} /> },
  { path:"/checkout/success", component: CheckoutSuccessPage  },
  { path:"/checkout/cancel",  component: CheckoutCancelPage   },
];

// ─── Blog extra CSS ───────────────────────────────────────────────────────────
const BLOG_EXTRA_CSS = `
.blog-filter-bar { display:flex; flex-wrap:wrap; gap:.5rem; margin-bottom:2rem; }
.blog-filter-btn {
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 14px; border-radius:100px;
  border:1.5px solid var(--border); background:none;
  font-size:.78rem; font-weight:600; color:var(--muted);
  cursor:pointer; transition:all .15s;
}
.blog-filter-btn:hover { border-color:var(--blue); color:var(--blue); }
.blog-filter-btn--on  { background:var(--blue); border-color:var(--blue); color:#fff; }
.blog-filter-count {
  background:rgba(255,255,255,.25); border-radius:100px;
  padding:0 6px; font-size:.68rem; font-weight:700;
}
.blog-filter-btn:not(.blog-filter-btn--on) .blog-filter-count { background:var(--border); color:var(--muted); }
.blog-grid--full { grid-template-columns:repeat(auto-fill, minmax(320px,1fr)); }
.blog-card-title { font-size:1rem; font-weight:700; color:var(--navy); margin:0 0 .5rem; line-height:1.35; }
.blog-card-footer { padding:.75rem 1.25rem; border-top:1px solid var(--border); }
.blog-read-more  { font-size:.78rem; font-weight:700; color:var(--blue); }
.blog-related { max-width:780px; margin:3rem auto 0; padding:2rem 0 0; border-top:1px solid var(--border); }
.blog-related__title {
  font-size:.6rem; font-weight:700; letter-spacing:.14em;
  text-transform:uppercase; color:var(--muted); margin-bottom:1rem;
}
.blog-related__grid { display:grid; grid-template-columns:1fr 1fr; gap:1rem; }
@media(max-width:600px) { .blog-related__grid { grid-template-columns:1fr; } }
.blog-related__card {
  background:var(--off); border:1px solid var(--border); border-radius:10px;
  padding:1rem 1.25rem; cursor:pointer; transition:border-color .15s;
}
.blog-related__card:hover { border-color:var(--blue); }
.blog-related__card h3 { font-size:.86rem; font-weight:700; color:var(--navy); margin:.5rem 0 .75rem; line-height:1.35; }
`;

// ─── Dashboard CSS ────────────────────────────────────────────────────────────
const DASH_CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
.dash-sr-only{position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;white-space:nowrap!important;border:0!important;}
.dash-skip{position:absolute;top:-999px;left:1rem;z-index:9999;padding:8px 18px;background:#fff;color:#1850a0;font-family:'IBM Plex Sans',system-ui,sans-serif;font-weight:700;font-size:.85rem;border:2px solid #1850a0;border-radius:5px;text-decoration:none;transition:top .1s;}
.dash-skip:focus{top:.75rem;}
.dash-shell{--d-navy:#0b1f35;--d-blue:#1850a0;--d-blue-dk:#103578;--d-blue-lt:#dceafc;--d-blue-md:#2563c4;--d-bg:#f0f3f8;--d-surf:#ffffff;--d-border:#cdd7e8;--d-border2:#e4eaf3;--d-text:#0d1c2b;--d-muted:#4a5f76;--d-label:#3a526a;--d-red:#a71c1c;--d-green:#145228;--d-green-bg:#d1fae5;--d-green-bd:#6ee7b7;--d-r:8px;--d-font:'IBM Plex Sans',system-ui,-apple-system,sans-serif;--d-mono:'IBM Plex Mono','Fira Code','Courier New',monospace;display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--d-bg);font-family:var(--d-font);color:var(--d-text);font-size:14px;line-height:1.5;}
.dash-topbar{display:flex;align-items:center;justify-content:space-between;gap:1rem;height:52px;padding:0 1.25rem;background:var(--d-surf);border-bottom:2px solid var(--d-navy);flex-shrink:0;z-index:20;}
.dash-topbar__logo{display:inline-flex;align-items:center;gap:7px;height:36px;padding:0 13px;background:var(--d-navy);border:none;border-radius:5px;color:#fff;font-family:var(--d-font);font-size:.8rem;font-weight:700;cursor:pointer;letter-spacing:.02em;transition:background .15s;flex-shrink:0;}
.dash-topbar__logo:hover{background:#17324f;}
.dash-topbar__status{display:flex;align-items:center;gap:.4rem;flex:1;justify-content:center;}
.dash-topbar__actions{display:flex;align-items:center;gap:.4rem;flex-shrink:0;}
.dash-chip{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:100px;font-size:.68rem;font-weight:700;letter-spacing:.04em;border:1px solid transparent;}
.dash-chip--pro{background:var(--d-blue);color:#fff;}
.dash-chip--day{background:var(--d-blue-lt);color:var(--d-blue-dk);border-color:#b3d0f5;}
.dash-chip--warn{background:#fee2e2;color:var(--d-red);border-color:#fca5a5;}
.dash-chip--ok{background:var(--d-green-bg);color:var(--d-green);border-color:var(--d-green-bd);}
.dash-tabbar{background:var(--d-surf);border-bottom:1px solid var(--d-border);flex-shrink:0;}
.dash-tabbar__inner{display:flex;padding:0 1.25rem;}
.dash-tabbar__tab{display:inline-flex;align-items:center;white-space:nowrap;height:44px;padding:0 16px;font-family:var(--d-font);font-size:.78rem;font-weight:600;color:var(--d-muted);background:none;border:none;border-bottom:2px solid transparent;cursor:pointer;margin-bottom:-1px;transition:color .15s,border-color .15s;}
.dash-tabbar__tab--on{color:var(--d-blue);border-bottom-color:var(--d-blue);}
.dash-tabbar__tab:hover:not(.dash-tabbar__tab--on){color:var(--d-text);}
.dash-tabbar__tab:focus-visible{outline:2.5px solid var(--d-blue);outline-offset:2px;border-radius:3px;}
.dash-body{display:flex;flex:1;overflow:hidden;}
.dash-sidebar{width:248px;flex-shrink:0;background:var(--d-surf);border-right:1px solid var(--d-border);display:flex;flex-direction:column;padding:12px 12px 10px;overflow-y:auto;}
.dash-sb-group{display:flex;flex-direction:column;}
.dash-sb-group__label{font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--d-label);margin-bottom:7px;display:block;}
.dash-sb-div{height:1px;background:var(--d-border2);margin:9px 0;}
.dash-sb-spacer{flex:1;}
.dash-sb-meta{display:flex;flex-direction:column;gap:2px;}
.dash-sb-meta__row{display:flex;justify-content:space-between;align-items:baseline;font-size:.7rem;color:var(--d-muted);}
.dash-sb-meta__row dt{font-weight:500;}
.dash-sb-meta__row dd{font-family:var(--d-mono);font-weight:600;font-size:.69rem;color:var(--d-text);}
.dash-f{margin-bottom:7px;}
.dash-f__lbl{display:block;font-size:.61rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--d-label);margin-bottom:3px;}
.dash-f__req{color:var(--d-red);}
.dash-f__hint{font-size:.62rem;color:var(--d-muted);display:block;margin-bottom:3px;line-height:1.4;}
.dash-inp{display:block;width:100%;height:32px;padding:0 8px;border:1.5px solid var(--d-border);border-radius:5px;font-size:.79rem;font-family:var(--d-font);color:var(--d-text);background:#fff;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box;}
.dash-inp:focus-visible{border-color:var(--d-blue);box-shadow:0 0 0 2.5px rgba(24,80,160,.18);}
.dash-inp:disabled{background:#edf1f7;color:var(--d-muted);cursor:not-allowed;border-color:#dde5f0;}
.dash-inp--sel{appearance:auto;cursor:pointer;}
.dash-color-row{display:flex;align-items:center;gap:8px;}
.dash-color-swatch{width:32px;height:32px;border:1.5px solid var(--d-border);border-radius:5px;padding:2px;background:none;cursor:pointer;flex-shrink:0;}
.dash-color-swatch:focus-visible{box-shadow:0 0 0 2.5px rgba(24,80,160,.22);outline:none;}
.dash-color-swatch:disabled{opacity:.5;cursor:not-allowed;}
.dash-color-val{font-size:.74rem;color:var(--d-muted);font-family:var(--d-mono);font-weight:500;}
.dash-color-swatch-sm{width:13px;height:13px;border-radius:3px;border:1px solid rgba(0,0,0,.1);flex-shrink:0;display:inline-block;margin-right:4px;}
.dash-btn-ghost{background:none;border:1.5px solid var(--d-border);border-radius:5px;color:var(--d-muted);font-family:var(--d-font);font-size:.75rem;font-weight:600;cursor:pointer;padding:0 12px;height:34px;transition:border-color .15s,color .15s,background .15s;}
.dash-btn-ghost:hover{border-color:var(--d-blue);color:var(--d-blue);background:var(--d-blue-lt);}
.dash-btn-danger{background:none;border:1.5px solid #fca5a5;border-radius:5px;color:var(--d-red);font-family:var(--d-font);font-size:.75rem;font-weight:600;cursor:pointer;padding:0 12px;height:34px;transition:background .15s;}
.dash-btn-danger:hover{background:#fee2e2;}
.dash-btn-reset{background:none;border:none;font-family:var(--d-font);font-size:.69rem;color:var(--d-muted);cursor:pointer;padding:4px 0;text-align:left;text-decoration:underline;text-underline-offset:2px;transition:color .15s;flex-shrink:0;}
.dash-btn-reset:hover{color:var(--d-red);}
.dash-btn-copy{display:inline-flex;align-items:center;gap:6px;margin-top:8px;padding:8px 16px;height:38px;border:1.5px solid var(--d-blue);border-radius:6px;background:var(--d-blue-lt);color:var(--d-blue-dk);font-family:var(--d-font);font-size:.76rem;font-weight:700;cursor:pointer;letter-spacing:.01em;transition:background .15s,border-color .15s,color .15s;}
.dash-btn-copy:hover{background:#c5dcf9;}
.dash-btn-copy--ok{background:var(--d-green-bg);border-color:var(--d-green-bd);color:var(--d-green);}
.dash-btn-convert{display:block;width:100%;margin-top:8px;padding:11px;height:44px;border:none;border-radius:var(--d-r);background:linear-gradient(135deg,var(--d-blue),var(--d-blue-md));color:#fff;font-family:var(--d-font);font-size:.82rem;font-weight:700;cursor:pointer;letter-spacing:.02em;transition:opacity .15s;}
.dash-btn-convert:hover{opacity:.88;}
.dash-main{flex:1;overflow-y:auto;background:var(--d-bg);outline:none;}
[role="tabpanel"][hidden]{display:none;}
.dash-panel{padding:1.75rem 2rem;max-width:860px;}
.dash-panel__hdr{margin-bottom:1.5rem;}
.dash-panel__hdr h1{font-size:1.25rem;font-weight:700;color:var(--d-navy);margin-bottom:3px;letter-spacing:-.02em;}
.dash-panel__hdr p{font-size:.82rem;color:var(--d-muted);line-height:1.65;margin:0;}
.dash-card{background:var(--d-surf);border:1.5px solid var(--d-border);border-radius:10px;padding:1.25rem;margin-bottom:1.1rem;}
.dash-card__title{font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--d-label);margin-bottom:1rem;}
.dash-toggle-grid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--d-border2);border:1px solid var(--d-border2);border-radius:7px;overflow:hidden;}
@media(max-width:680px){.dash-toggle-grid{grid-template-columns:1fr;}}
.dash-tog-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 14px;background:#fff;transition:background .12s;}
.dash-tog-row:hover{background:#f7f9fd;}
.dash-tog-row__text{display:flex;flex-direction:column;gap:1px;flex:1;}
.dash-tog-row__name{font-size:.8rem;font-weight:600;color:var(--d-text);}
.dash-tog-row__desc{font-size:.67rem;color:var(--d-muted);line-height:1.4;}
.dash-tog{position:relative;flex-shrink:0;display:inline-block;cursor:pointer;}
.dash-tog input[type="checkbox"]{position:absolute;opacity:0;width:100%;height:100%;margin:0;cursor:pointer;z-index:1;}
.dash-tog__track{display:block;width:40px;height:22px;border-radius:11px;background:#cdd7e8;border:1.5px solid #b8c6da;transition:background .18s,border-color .18s;position:relative;}
.dash-tog input:checked+.dash-tog__track{background:var(--d-blue);border-color:var(--d-blue-dk);}
.dash-tog input:disabled+.dash-tog__track{opacity:.4;cursor:not-allowed;}
.dash-tog:focus-within .dash-tog__track{box-shadow:0 0 0 2.5px rgba(24,80,160,.22);}
.dash-tog__thumb{position:absolute;width:14px;height:14px;border-radius:50%;background:#fff;top:50%;transform:translateY(-50%);left:3px;transition:left .16s cubic-bezier(.4,0,.2,1);box-shadow:0 1px 3px rgba(0,0,0,.2);pointer-events:none;}
.dash-tog input:checked~.dash-tog__thumb{left:21px;}
.dash-adv-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 1.25rem;}
@media(max-width:680px){.dash-adv-grid{grid-template-columns:1fr;}}
.dash-adv-grid .dash-f{margin-bottom:10px;}
.dash-adv-grid .dash-inp{height:34px;}
.dash-dropzone{display:block;border:2px dashed var(--d-border);border-radius:9px;padding:2.5rem 1.5rem;text-align:center;cursor:pointer;background:#fafbfd;transition:border-color .18s,background .18s;outline:none;}
.dash-dropzone:hover,.dash-dropzone--drag{border-color:var(--d-blue);background:var(--d-blue-lt);}
.dash-dropzone:focus-visible{border-color:var(--d-blue);box-shadow:0 0 0 2.5px rgba(24,80,160,.22);}
.dash-dz__icon{font-size:2rem;margin-bottom:8px;display:block;}
.dash-dz__text{font-size:.84rem;color:var(--d-muted);display:block;margin-bottom:4px;}
.dash-dz__link{color:var(--d-blue);font-weight:700;text-decoration:underline;text-underline-offset:2px;}
.dash-dz__name{font-size:.84rem;font-weight:700;color:var(--d-text);display:block;margin-bottom:4px;}
.dash-dz__sub{font-size:.72rem;color:var(--d-label);display:block;}
.dash-tip-list{list-style:none;display:flex;flex-direction:column;gap:10px;}
.dash-tip-list li{display:flex;align-items:flex-start;gap:10px;font-size:.82rem;color:var(--d-muted);line-height:1.6;}
.dash-tip-dot{width:6px;height:6px;border-radius:50%;background:var(--d-blue);flex-shrink:0;margin-top:6px;}
.dash-codeblock{background:#0d1b2a;border-radius:9px;overflow:hidden;border:1px solid rgba(255,255,255,.06);margin-bottom:4px;}
.dash-codeblock__bar{display:flex;align-items:center;gap:5px;background:#132333;padding:7px 12px;border-bottom:1px solid rgba(255,255,255,.06);}
.dash-codeblock__dot{width:10px;height:10px;border-radius:50%;display:inline-block;}
.dash-codeblock__file{font-size:.66rem;color:rgba(255,255,255,.32);font-family:var(--d-mono);margin-left:4px;}
.dash-codeblock__pre{margin:0;padding:1rem 1.25rem;font-family:var(--d-mono);font-size:.74rem;line-height:1.9;color:#ddeaf6;white-space:pre;overflow-x:auto;tab-size:2;outline:none;}
.dash-codeblock__pre:focus-visible{outline:2.5px solid #5ba4f5;outline-offset:-2.5px;}
.dash-note{font-size:.8rem;color:var(--d-muted);line-height:1.75;margin-bottom:.85rem;}
.dash-note code{background:var(--d-bg);padding:1px 5px;border-radius:3px;font-family:var(--d-mono);font-size:.8em;color:var(--d-blue-dk);border:1px solid var(--d-border);}
.dash-note strong{font-weight:700;color:var(--d-text);}
.dash-browser{border:1.5px solid var(--d-border);border-radius:10px;overflow:hidden;box-shadow:0 3px 18px rgba(11,46,74,.08);margin-bottom:1.1rem;}
.dash-browser__bar{background:#e5eaf3;padding:8px 12px;display:flex;align-items:center;gap:7px;border-bottom:1px solid var(--d-border);}
.dash-browser__dots{display:flex;gap:5px;}
.dash-browser__url{flex:1;background:#fff;border:1px solid var(--d-border);border-radius:5px;padding:3px 10px;font-size:.69rem;color:var(--d-muted);font-family:var(--d-mono);}
.dash-browser__body{background:#eef2f8;padding:1.25rem;}
.dash-modal{background:#fff;border-radius:9px;border:1.5px solid var(--d-border);overflow:hidden;box-shadow:0 2px 12px rgba(11,46,74,.07);}
.dash-modal__hdr{display:flex;align-items:center;gap:10px;padding:.9rem 1.1rem;border-bottom:1px solid var(--d-border);}
.dash-modal__logo{max-height:32px;object-fit:contain;}
.dash-modal__title{font-size:.86rem;font-weight:700;color:var(--d-navy);}
.dash-modal__sub{font-size:.69rem;color:var(--d-muted);margin-top:1px;}
.dash-modal__close{margin-left:auto;width:24px;height:24px;display:flex;align-items:center;justify-content:center;background:var(--d-bg);border-radius:50%;font-size:.72rem;color:var(--d-muted);flex-shrink:0;}
.dash-modal__body{padding:1.1rem;}
.dash-modal__foot{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:8px;padding:.875rem 1.1rem;border-top:1px solid var(--d-border);background:var(--d-bg);}
.dash-wcag-chip{display:inline-flex;align-items:center;gap:5px;background:var(--d-green-bg);border:1px solid var(--d-green-bd);border-radius:100px;padding:3px 9px;font-size:.65rem;font-weight:700;color:var(--d-green);margin-bottom:12px;}
.dash-wcag-dot{width:6px;height:6px;border-radius:50%;background:#22c55e;display:inline-block;}
.dash-mock-lines{display:flex;flex-direction:column;gap:7px;}
.dash-mock-line{height:7px;border-radius:4px;background:#e0e8f0;}
.dash-mock-line--h{height:11px;width:55%;background:#c5d3e5;margin-bottom:3px;}
.dash-preview-tag{background:var(--d-blue-lt);color:var(--d-blue-dk);font-size:.63rem;font-weight:700;padding:2px 8px;border-radius:100px;border:1px solid #b3d0f5;}
.dash-preview-act{display:inline-flex;align-items:center;padding:4px 10px;border-radius:5px;border:1.5px solid;font-size:.71rem;font-weight:600;background:transparent;}
.dash-cfg-summary{background:var(--d-surf);border:1.5px solid var(--d-border);border-radius:10px;padding:1.1rem 1.4rem;}
.dash-cfg-summary__title{font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--d-label);margin-bottom:.875rem;}
.dash-cfg-summary__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:.6rem 1.25rem;margin:0;padding:0;}
.dash-cfg-summary__item{display:flex;flex-direction:column;gap:2px;}
.dash-cfg-summary__item dt{font-size:.59rem;color:var(--d-label);font-weight:700;text-transform:uppercase;letter-spacing:.06em;}
.dash-cfg-summary__item dd{font-size:.79rem;color:var(--d-text);font-weight:600;display:flex;align-items:center;}
.dash-checklist{background:var(--d-surf);border:1.5px solid var(--d-border);border-radius:10px;padding:1.25rem;margin-top:1.1rem;}
.dash-checklist__title{display:flex;align-items:center;justify-content:space-between;font-size:.6rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--d-label);margin-bottom:.875rem;}
.dash-cl-progress{background:var(--d-bg);border:1px solid var(--d-border);border-radius:100px;padding:2px 9px;font-size:.63rem;font-weight:700;color:var(--d-blue-dk);letter-spacing:0;text-transform:none;}
.dash-cl-list{list-style:none;margin:0;padding:0;}
.dash-cl-item{display:flex;align-items:center;gap:9px;padding:7px 0;border-bottom:1px solid var(--d-bg);font-size:.8rem;color:var(--d-muted);font-weight:400;}
.dash-cl-item:last-child{border-bottom:none;}
.dash-cl-item--done{color:var(--d-green);font-weight:600;}
.dash-cl-icon{width:20px;height:20px;border-radius:50%;background:var(--d-bg);border:1.5px solid var(--d-border);display:inline-flex;align-items:center;justify-content:center;font-size:.68rem;font-weight:700;color:var(--d-label);flex-shrink:0;}
.dash-cl-item--done .dash-cl-icon{background:var(--d-green-bg);border-color:var(--d-green-bd);color:var(--d-green);}
.dash-main::-webkit-scrollbar{width:5px;}
.dash-main::-webkit-scrollbar-thumb{background:var(--d-border);border-radius:10px;}
@media(prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;transition-duration:.01ms!important;}}
@media(prefers-color-scheme:dark){
  .dash-shell{--d-bg:#0e1820;--d-surf:#16242f;--d-border:#243547;--d-border2:#1c2e3e;--d-navy:#0c1d31;--d-text:#ddeaf6;--d-muted:#8db5cc;--d-label:#7aaabf;--d-blue-lt:#19304a;--d-blue-dk:#7bbcf8;}
  .dash-inp{background:#1b3040;color:var(--d-text);border-color:var(--d-border);}
  .dash-inp:disabled{background:#111d28;}
  .dash-tog-row{background:var(--d-surf);}
  .dash-tog-row:hover{background:#1d3244;}
  .dash-tog__track{background:#243547;border-color:#324d62;}
  .dash-dropzone{background:var(--d-surf);}
}
`;

// ─── App shell ────────────────────────────────────────────────────────────────
function AppInner() {
  const pathname   = usePathname();
  const matched    = matchRoutes(ROUTES, pathname);
  const Component  = matched?.component;
  const isCheckout = pathname.startsWith("/checkout");
  const isDash     = pathname === "/dashboard";
  const isBlogPost = pathname.startsWith("/blog/");

  if (isDash) return <Dashboard />;

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <style>{BLOG_EXTRA_CSS}</style>
      {!isCheckout && <Nav />}
      <main style={!isCheckout && !isBlogPost ? { marginTop:64 } : {}}>
        {Component ? <Component {...(matched.params || {})} /> : <NotFoundPage />}
      </main>
      {!isCheckout && <Footer />}
    </>
  );
}

function App() {
  return (
    <RouterProvider>
      <AppInner />
    </RouterProvider>
  );
}

const mountApp = () => {
  const rootEl = document.getElementById("root");
  if (rootEl) {
    ReactDOM.createRoot(rootEl).render(<App />);
  }
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountApp);
} else {
  mountApp();
}